// bluaIQ — M1: Takeoff Analysis Module
// Upload architectural plans, AI extracts material quantities

import { useState, useRef, useEffect } from 'react'
import { nonPurchasableQuantitySum } from '../shared/takeoffSubtotalRule'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp, Trash2, ShoppingCart, MapPin, Info, FlaskConical } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { sortCategoryKeys } from '../utils/takeoffCategoryColors'
import { apiFetch } from '../utils/api'
import { getDiagnosticV3Config } from '../takeoff-diagnostic-v3/config'
import { isDiagnosticV3VisibleToClient } from '../takeoff-diagnostic-v3/clientFlags'
// Canonical project-location resolver. Pure function (no DB, no
// network) and already used by server-side pricing scoping
// (`getScopedPricePatterns`). We import it here so the FE applies the
// EXACT SAME rule: DFW pricing is only valid when the resolver
// returns `metro === 'DFW'` AND `confidence === 'explicit'` — i.e.
// state is TX and the city is in the supported DFW allow-list
// (`SUPPORTED_DFW_CITY_KEYS` inside the resolver). A Texas project
// outside the supported set (Austin, Houston, El Paso, …) MUST NOT
// receive DFW pricing. See docs/specs/FIXES-pricing-fallback-policy.md
// §2 for the policy.
import { resolveProjectLocationScope } from '../server/utils/projectLocationScope'

// ── TYPES ─────────────────────────────────────────

interface TakeoffAlternative {
  name: string
  estimatedCost: number
  unit: string
  notes?: string
}

interface TakeoffItem {
  id: string
  name: string
  quantity: number
  unit: string
  category: string
  csiCode?: string
  description?: string
  unitPrice?: number | null
  alternatives?: TakeoffAlternative[] | string[]
  aiConfidence?: number
  isApproved: boolean
}

interface TakeoffShadowDrywall {
  normalized?: {
    regularBoardSf: number
    typeXBoardSf: number
    halfInSheetCount: number
    typeXSheetCount: number
    jointCompoundBuckets: number
    paperTapeRolls: number
    screwBoxes: number
    cornerBeadLf: number
    primerGallons: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowRoofing {
  normalized?: {
    baseRoofSquares: number
    shingleSquares: number
    underlaymentRolls: number
    starterBundles: number
    ridgeVentLf: number
    dripEdgeLf: number
    class4ImpactRecommended: boolean
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowHvac {
  normalized?: {
    systemUnitCount: number
    tonnage: number
    heatPumpCount: number
    furnaceCount: number
    airHandlerCount: number
    ductLf: number
    refrigerantLineLf: number
    thermostatCount: number
    ventilationFanCount: number
    mechanicalVentilationRequired: boolean
    efficiencyBaseline: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowLightingFans {
  normalized?: {
    totalFixtureCount: number
    recessedLightCount: number
    ceilingFanCount: number
    exhaustFanCount: number
    decorativeFixtureCount: number
    deviceTrimCount: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowCabinets {
  normalized?: {
    totalUnitCount: number
    baseCabinetCount: number
    wallCabinetCount: number
    vanityCount: number
    islandCount: number
    runLf: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowCountertops {
  normalized?: {
    totalAreaSf: number
    kitchenAreaSf: number
    vanityAreaSf: number
    islandAreaSf: number
    backsplashLf: number
    sinkCutoutCount: number
    seamAllowanceCount: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowFlooring {
  normalized?: {
    totalAreaSf: number
    tileAreaSf: number
    lvpAreaSf: number
    carpetAreaSf: number
    hardwoodAreaSf: number
    underlaymentAreaSf: number
    transitionCount: number
    wasteAreaSf: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowPaint {
  normalized?: {
    paintableAreaSf: number
    interiorAreaSf: number
    exteriorAreaSf: number
    primerAreaSf: number
    finishGallons: number
    primerGallons: number
    caulkTubeCount: number
    touchupGallons: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowAppliances {
  normalized?: {
    totalUnitCount: number
    kitchenPackageCount: number
    laundryUnitCount: number
    refrigeratorCount: number
    rangeCount: number
    dishwasherCount: number
    microwaveCount: number
    hoodCount: number
    installKitCount: number
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowElectricalRough {
  normalized?: {
    wireLf: number
    panelCount: number
    breakerCount: number
    roughBoxCount: number
    surgeProtectionRequired: boolean
    panelServiceBaseline: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowPlumbingRough {
  normalized?: {
    supplyLf: number
    drainLf: number
    ventLf: number
    gasLineLf: number
    waterHeaterCount: number
    supplyMaterialDefault: string
    drainDefault: string
    drainWorkflow?: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowPlumbingFixtures {
  normalized?: {
    totalFixtureCount: number
    toiletCount: number
    sinkCount: number
    faucetCount: number
    showerTrimCount: number
    tubCount: number
    disposalCount: number
    waterHeaterCount: number
    finishSpecBaseline: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowInsulation {
  normalized?: {
    wallAreaSf: number
    atticAreaSf: number
    battAreaSf: number
    sprayFoamAreaSf: number
    blownAtticAreaSf: number
    wallRValue: string
    atticRValue: string
    energyPathControlled: boolean
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowExterior {
  normalized?: {
    claddingAreaSf: number
    orderAreaSf: number
    trimLf: number
    housewrapRolls: number
    weatherBarrierRequired: boolean
    hardiePreferred: boolean
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowWindows {
  normalized?: {
    totalUnits: number
    operableUnits: number
    fixedUnits: number
    lowERequired: boolean
    performanceBaseline?: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowDoors {
  normalized?: {
    totalUnits: number
    interiorUnits: number
    exteriorUnits: number
    garageDoorUnits: number
    hardwareSets: number
    weatherstrippingRequired: boolean
    energyBaseline?: string
  }
  bom?: Array<{
    category: string
    name: string
    quantity: number
    unit: string
    notes?: string
  }>
}

interface TakeoffShadowPayload {
  error?: string
  comparison?: {
    exterior?: {
      v1: {
        claddingAreaSf: number
        trimLf: number
        housewrapRolls: number
      }
      shadow: {
        orderAreaSf: number
        trimLf: number
        housewrapRolls: number
        bomItemCount: number
      }
      delta: {
        claddingAreaSf: number
        trimLf: number
        housewrapRolls: number
      }
    }
    roofing?: {
      v1: {
        shingleSquares: number
        ridgeVentLf: number
        dripEdgeLf: number
      }
      shadow: {
        shingleSquares: number
        underlaymentRolls: number
        starterBundles: number
        ridgeVentLf: number
        dripEdgeLf: number
        bomItemCount: number
      }
      delta: {
        shingleSquares: number
        ridgeVentLf: number
        dripEdgeLf: number
      }
    }
    hvac?: {
      v1: {
        systemUnitCount: number
        tonnage: number
        ductLf: number
        refrigerantLineLf: number
        thermostatCount: number
      }
      shadow: {
        systemUnitCount: number
        tonnage: number
        heatPumpCount: number
        furnaceCount: number
        airHandlerCount: number
        ductLf: number
        refrigerantLineLf: number
        thermostatCount: number
        mechanicalVentilationRequired: boolean
        efficiencyBaseline: string
        bomItemCount: number
      }
      delta: {
        systemUnitCount: number
        tonnage: number
        ductLf: number
        refrigerantLineLf: number
        thermostatCount: number
      }
    }
    lightingFans?: {
      v1: {
        totalFixtureCount: number
        recessedLightCount: number
        ceilingFanCount: number
        decorativeFixtureCount: number
      }
      shadow: {
        totalFixtureCount: number
        recessedLightCount: number
        ceilingFanCount: number
        exhaustFanCount: number
        decorativeFixtureCount: number
        deviceTrimCount: number
        bomItemCount: number
      }
      delta: {
        totalFixtureCount: number
        recessedLightCount: number
        ceilingFanCount: number
        decorativeFixtureCount: number
      }
    }
    cabinets?: {
      v1: {
        totalUnitCount: number
        baseCabinetCount: number
        wallCabinetCount: number
        vanityCount: number
        islandCount: number
        runLf: number
      }
      shadow: {
        totalUnitCount: number
        baseCabinetCount: number
        wallCabinetCount: number
        vanityCount: number
        islandCount: number
        runLf: number
        bomItemCount: number
      }
      delta: {
        totalUnitCount: number
        baseCabinetCount: number
        wallCabinetCount: number
        vanityCount: number
        islandCount: number
        runLf: number
      }
    }
    countertops?: {
      v1: {
        totalAreaSf: number
        kitchenAreaSf: number
        vanityAreaSf: number
        islandAreaSf: number
        backsplashLf: number
      }
      shadow: {
        totalAreaSf: number
        kitchenAreaSf: number
        vanityAreaSf: number
        islandAreaSf: number
        backsplashLf: number
        sinkCutoutCount: number
        seamAllowanceCount: number
        bomItemCount: number
      }
      delta: {
        totalAreaSf: number
        kitchenAreaSf: number
        vanityAreaSf: number
        islandAreaSf: number
        backsplashLf: number
      }
    }
    flooring?: {
      v1: {
        totalAreaSf: number
        tileAreaSf: number
        lvpAreaSf: number
        carpetAreaSf: number
        hardwoodAreaSf: number
        transitionCount: number
      }
      shadow: {
        totalAreaSf: number
        tileAreaSf: number
        lvpAreaSf: number
        carpetAreaSf: number
        hardwoodAreaSf: number
        underlaymentAreaSf: number
        transitionCount: number
        wasteAreaSf: number
        bomItemCount: number
      }
      delta: {
        totalAreaSf: number
        tileAreaSf: number
        lvpAreaSf: number
        carpetAreaSf: number
        hardwoodAreaSf: number
        transitionCount: number
      }
    }
    paint?: {
      v1: {
        paintableAreaSf: number
        interiorAreaSf: number
        exteriorAreaSf: number
        primerAreaSf: number
        finishGallons: number
        primerGallons: number
        caulkTubeCount: number
      }
      shadow: {
        paintableAreaSf: number
        interiorAreaSf: number
        exteriorAreaSf: number
        primerAreaSf: number
        finishGallons: number
        primerGallons: number
        caulkTubeCount: number
        touchupGallons: number
        bomItemCount: number
      }
      delta: {
        paintableAreaSf: number
        finishGallons: number
        primerGallons: number
        caulkTubeCount: number
      }
    }
    appliances?: {
      v1: {
        totalUnitCount: number
        kitchenPackageCount: number
        laundryUnitCount: number
        refrigeratorCount: number
        rangeCount: number
        dishwasherCount: number
        microwaveCount: number
        hoodCount: number
      }
      shadow: {
        totalUnitCount: number
        kitchenPackageCount: number
        laundryUnitCount: number
        refrigeratorCount: number
        rangeCount: number
        dishwasherCount: number
        microwaveCount: number
        hoodCount: number
        installKitCount: number
        bomItemCount: number
      }
      delta: {
        totalUnitCount: number
        kitchenPackageCount: number
        laundryUnitCount: number
        refrigeratorCount: number
        rangeCount: number
      }
    }
    plumbingRough?: {
      v1: {
        supplyLf: number
        drainLf: number
        ventLf: number
        waterHeaterCount: number
        gasLineLf: number
      }
      shadow: {
        supplyLf: number
        drainLf: number
        ventLf: number
        gasLineLf: number
        waterHeaterCount: number
        drainDefault: string
        bomItemCount: number
      }
      delta: {
        supplyLf: number
        drainLf: number
        ventLf: number
        gasLineLf: number
      }
    }
    plumbingFixtures?: {
      v1: {
        totalFixtureCount: number
        toiletCount: number
        sinkCount: number
        faucetCount: number
        showerTrimCount: number
      }
      shadow: {
        totalFixtureCount: number
        toiletCount: number
        sinkCount: number
        faucetCount: number
        showerTrimCount: number
        tubCount: number
        disposalCount: number
        waterHeaterCount: number
        bomItemCount: number
      }
      delta: {
        totalFixtureCount: number
        toiletCount: number
        faucetCount: number
        showerTrimCount: number
      }
    }
    electricalRough?: {
      v1: {
        wireLf: number
        panelCount: number
        breakerCount: number
        roughBoxCount: number
      }
      shadow: {
        wireLf: number
        panelCount: number
        breakerCount: number
        roughBoxCount: number
        surgeProtectionRequired: boolean
        bomItemCount: number
      }
      delta: {
        wireLf: number
        panelCount: number
        breakerCount: number
        roughBoxCount: number
      }
    }
    insulation?: {
      v1: {
        wallAreaSf: number
        atticAreaSf: number
        sprayFoamAreaSf: number
      }
      shadow: {
        wallAreaSf: number
        atticAreaSf: number
        battAreaSf: number
        sprayFoamAreaSf: number
        bomItemCount: number
      }
      delta: {
        wallAreaSf: number
        atticAreaSf: number
        sprayFoamAreaSf: number
      }
    }
    drywall?: {
      v1: {
        regularSheets: number
        typeXSheets: number
        cornerBeadLf: number
      }
      shadow: {
        regularSheets: number
        typeXSheets: number
        cornerBeadLf: number
        bomItemCount: number
        jointCompoundBuckets: number
      }
      delta: {
        regularSheets: number
        typeXSheets: number
        cornerBeadLf: number
      }
    }
    doors?: {
      v1: {
        totalUnits: number
        interiorUnits: number
        exteriorUnits: number
        garageDoorUnits: number
      }
      shadow: {
        totalUnits: number
        interiorUnits: number
        exteriorUnits: number
        garageDoorUnits: number
        hardwareSets: number
        bomItemCount: number
      }
      delta: {
        totalUnits: number
        exteriorUnits: number
        garageDoorUnits: number
      }
    }
    windows?: {
      v1: {
        totalUnits: number
        operableUnits: number
      }
      shadow: {
        totalUnits: number
        operableUnits: number
        fixedUnits: number
        bomItemCount: number
      }
      delta: {
        totalUnits: number
        operableUnits: number
      }
    }
  }
  facts?: {
    schemaVersion: string
    systems?: {
      exterior?: {
        claddingAreaSf: number
        trimLf?: number
        housewrapRollCount?: number
        notes?: string[]
      }
      roofing?: {
        roofAreaSquares: number
        ridgeVentLf?: number
        dripEdgeLf?: number
        notes?: string[]
      }
      hvac?: {
        systemUnitCount: number
        tonnage?: number
        heatPumpCount?: number
        furnaceCount?: number
        airHandlerCount?: number
        ductLf?: number
        refrigerantLineLf?: number
        thermostatCount?: number
        notes?: string[]
      }
      lightingFans?: {
        totalFixtureCount: number
        recessedLightCount?: number
        ceilingFanCount?: number
        exhaustFanCount?: number
        decorativeFixtureCount?: number
        deviceTrimCount?: number
        notes?: string[]
      }
      cabinets?: {
        totalUnitCount: number
        baseCabinetCount?: number
        wallCabinetCount?: number
        vanityCount?: number
        islandCount?: number
        runLf?: number
        notes?: string[]
      }
      countertops?: {
        totalAreaSf: number
        kitchenAreaSf?: number
        vanityAreaSf?: number
        islandAreaSf?: number
        backsplashLf?: number
        sinkCutoutCount?: number
        notes?: string[]
      }
      flooring?: {
        totalAreaSf: number
        tileAreaSf?: number
        lvpAreaSf?: number
        carpetAreaSf?: number
        hardwoodAreaSf?: number
        underlaymentAreaSf?: number
        transitionCount?: number
        notes?: string[]
      }
      paint?: {
        paintableAreaSf: number
        interiorAreaSf?: number
        exteriorAreaSf?: number
        primerAreaSf?: number
        finishGallons?: number
        primerGallons?: number
        caulkTubeCount?: number
        notes?: string[]
      }
      appliances?: {
        totalUnitCount: number
        kitchenPackageCount?: number
        laundryUnitCount?: number
        refrigeratorCount?: number
        rangeCount?: number
        dishwasherCount?: number
        microwaveCount?: number
        hoodCount?: number
        notes?: string[]
      }
      plumbingRough?: {
        supplyLf: number
        drainLf: number
        ventLf?: number
        waterHeaterCount?: number
        gasLineLf?: number
        notes?: string[]
      }
      plumbingFixtures?: {
        totalFixtureCount: number
        toiletCount?: number
        sinkCount?: number
        faucetCount?: number
        showerTrimCount?: number
        tubCount?: number
        disposalCount?: number
        waterHeaterCount?: number
        notes?: string[]
      }
      electricalRough?: {
        wireLf: number
        panelCount: number
        breakerCount?: number
        roughBoxCount?: number
        notes?: string[]
      }
      insulation?: {
        wallAreaSf: number
        atticAreaSf: number
        sprayFoamAreaSf?: number
        wallRValue?: string
        atticRValue?: string
        notes?: string[]
      }
      drywall?: {
        wallAreaSf: number
        ceilingAreaSf: number
        fireRatedWallAreaSf?: number
        cornerBeadLf?: number
        notes?: string[]
      }
      doors?: {
        unitCount: number
        interiorUnitCount?: number
        exteriorUnitCount?: number
        garageDoorUnitCount?: number
        hardwareSetCount?: number
        notes?: string[]
      }
      windows?: {
        unitCount: number
        operableUnitCount?: number
        notes?: string[]
      }
    }
  }
  result?: {
    version: string
    jurisdictionPackCity?: string
    systems?: {
      exterior?: TakeoffShadowExterior
      roofing?: TakeoffShadowRoofing
      hvac?: TakeoffShadowHvac
      lightingFans?: TakeoffShadowLightingFans
      cabinets?: TakeoffShadowCabinets
      countertops?: TakeoffShadowCountertops
      flooring?: TakeoffShadowFlooring
      paint?: TakeoffShadowPaint
      appliances?: TakeoffShadowAppliances
      plumbingRough?: TakeoffShadowPlumbingRough
      plumbingFixtures?: TakeoffShadowPlumbingFixtures
      electricalRough?: TakeoffShadowElectricalRough
      insulation?: TakeoffShadowInsulation
      doors?: TakeoffShadowDoors
      drywall?: TakeoffShadowDrywall
      windows?: TakeoffShadowWindows
    }
  }
}

interface Takeoff {
  id: string
  status: string
  totalItems?: number
  processingMs?: number
  createdAt: string
  items: TakeoffItem[]
  rawAiResponse?: {
    error?: string
    shadow?: TakeoffShadowPayload | null
  } | null
}

interface ProjectShadowSummary {
  totalCompared: number
  latest: {
    takeoffId: string
    createdAt: string
    deltaHvacSystems: number
    deltaHvacDuctLf: number
    shadowHvacBomItemCount: number
    deltaLightingFixtureCount: number
    shadowLightingBomItemCount: number
    deltaCabinetUnits: number
    shadowCabinetBomItemCount: number
    deltaCountertopSf: number
    shadowCountertopBomItemCount: number
    deltaFlooringSf: number
    shadowFlooringBomItemCount: number
    deltaPaintAreaSf: number
    shadowPaintBomItemCount: number
    deltaApplianceUnits: number
    shadowApplianceBomItemCount: number
    deltaPlumbingFixtureCount: number
    shadowPlumbingFixtureBomItemCount: number
    deltaPlumbingSupplyLf: number
    shadowPlumbingBomItemCount: number
    deltaElectricalWireLf: number
    shadowElectricalBomItemCount: number
    deltaRoofingSquares: number
    shadowRoofingBomItemCount: number
    deltaInsulationWall: number
    deltaInsulationAttic: number
    shadowInsulationBomItemCount: number
    deltaRegular: number
    deltaTypeX: number
    deltaCornerBead: number
    shadowBomItemCount: number
    deltaExteriorCladding: number
    shadowExteriorBomItemCount: number
    deltaDoorUnits: number
    deltaExteriorDoors: number
    shadowDoorBomItemCount: number
    deltaWindowUnits: number
    deltaOperableWindows: number
    shadowWindowBomItemCount: number
  } | null
  rows: Array<{
    takeoffId: string
    createdAt: string
    deltaHvacSystems: number
    deltaHvacDuctLf: number
    shadowHvacBomItemCount: number
    deltaLightingFixtureCount: number
    shadowLightingBomItemCount: number
    deltaCabinetUnits: number
    shadowCabinetBomItemCount: number
    deltaCountertopSf: number
    shadowCountertopBomItemCount: number
    deltaFlooringSf: number
    shadowFlooringBomItemCount: number
    deltaPaintAreaSf: number
    shadowPaintBomItemCount: number
    deltaApplianceUnits: number
    shadowApplianceBomItemCount: number
    deltaPlumbingFixtureCount: number
    shadowPlumbingFixtureBomItemCount: number
    deltaPlumbingSupplyLf: number
    shadowPlumbingBomItemCount: number
    deltaElectricalWireLf: number
    shadowElectricalBomItemCount: number
    deltaRoofingSquares: number
    shadowRoofingBomItemCount: number
    deltaInsulationWall: number
    deltaInsulationAttic: number
    shadowInsulationBomItemCount: number
    deltaRegular: number
    deltaTypeX: number
    deltaCornerBead: number
    shadowBomItemCount: number
    deltaExteriorCladding: number
    shadowExteriorBomItemCount: number
    deltaDoorUnits: number
    deltaExteriorDoors: number
    shadowDoorBomItemCount: number
    deltaWindowUnits: number
    deltaOperableWindows: number
    shadowWindowBomItemCount: number
  }>
}

// 19-category construction-sequence taxonomy (FOUNDATION → LANDSCAPING).
// Legacy aliases (mixed-case English, Spanish, old WINDOWS_DOORS, ELECTRICAL_FINISH) map onto the new set
// so existing DB rows still render until the SQL migration runs.
const CATEGORY_LABELS: Record<string, { en: string; es: string; color: string }> = {
  // ── New 19-category canonical set ─────────────────────────────────
  FOUNDATION:        { en: 'Foundation',             es: 'Cimentación',                  color: '#6B7280' },
  FRAMING:           { en: 'Framing',                es: 'Estructura',                   color: '#A16207' },
  EXTERIOR:          { en: 'Exterior / Siding',      es: 'Exterior / Revestimiento',     color: '#059669' },
  ROOFING:           { en: 'Roofing',                es: 'Techos',                       color: '#B91C1C' },
  WINDOWS:           { en: 'Windows',                es: 'Ventanas',                     color: '#0EA5E9' },
  DOORS:             { en: 'Doors',                  es: 'Puertas',                      color: '#7C3AED' },
  PLUMBING_ROUGH:    { en: 'Plumbing — Rough-In',    es: 'Preinstalación de Plomería',   color: '#06B6D4' },
  ELECTRICAL_ROUGH:  { en: 'Electrical — Rough-In',  es: 'Preinstalación Eléctrica',     color: '#F59E0B' },
  HVAC:              { en: 'HVAC',                   es: 'Clima',                        color: '#0D9488' },
  INSULATION:        { en: 'Insulation',             es: 'Aislamiento',                  color: '#EC4899' },
  DRYWALL:           { en: 'Drywall',                es: 'Tablaroca',                    color: '#D4A574' },
  PLUMBING_FIXTURES: { en: 'Plumbing — Fixtures',    es: 'Acabados de Plomería',         color: '#0891B2' },
  LIGHTING_FANS:     { en: 'Lighting & Fans',        es: 'Acabados Eléctricos',          color: '#EAB308' },
  PAINT:             { en: 'Paint',                  es: 'Pintura',                      color: '#8B5CF6' },
  CABINETS:          { en: 'Cabinets',               es: 'Gabinetes',                    color: '#EA580C' },
  COUNTERTOPS:       { en: 'Countertops',            es: 'Cubiertas',                    color: '#475569' },
  FLOORING:          { en: 'Flooring',               es: 'Pisos',                        color: '#78350F' },
  APPLIANCES:        { en: 'Appliances',             es: 'Electrodomésticos',            color: '#4338CA' },
  LANDSCAPING:       { en: 'Landscaping',            es: 'Jardinería',                   color: '#65A30D' },
  // ── Legacy aliases (backward compat — pre-migration DB rows) ──────
  SIDING:            { en: 'Exterior / Siding',      es: 'Exterior / Revestimiento',     color: '#059669' },
  WINDOWS_DOORS:     { en: 'Windows & Doors',        es: 'Ventanas y Puertas',           color: '#0EA5E9' },
  ELECTRICAL:        { en: 'Electrical — Rough-In',  es: 'Preinstalación Eléctrica',     color: '#F59E0B' },
  ELECTRICAL_FINISH: { en: 'Lighting & Fans',        es: 'Acabados Eléctricos',          color: '#EAB308' },
  PLUMBING:          { en: 'Plumbing — Rough-In',    es: 'Preinstalación de Plomería',   color: '#06B6D4' },
  PLUMBING_FINISH:   { en: 'Plumbing — Fixtures',    es: 'Acabados de Plomería',         color: '#0891B2' },
  MEP_ROUGH:         { en: 'MEP Rough',              es: 'Instalaciones Brutas',         color: '#06B6D4' },
  FINISHES:          { en: 'Finishes',               es: 'Acabados',                     color: '#22C55E' },
  HARDWARE:          { en: 'Hardware',               es: 'Ferretería',                   color: '#64748B' },
  SITEWORK:          { en: 'Sitework',               es: 'Obra Exterior',                color: '#92400E' },
  MISCELLANEOUS:     { en: 'Miscellaneous',          es: 'Varios',                       color: '#9E9890' },
  Miscellaneous:     { en: 'Miscellaneous',          es: 'Varios',                       color: '#9E9890' },
  OTHER:             { en: 'Other',                  es: 'Otro',                         color: '#9E9890' },
  Kitchen:           { en: 'Kitchen',                es: 'Cocina',                       color: '#009688' },
  Bathroom:          { en: 'Bathroom',               es: 'Baño',                         color: '#00BCD4' },
  // ── Spanish keys returned by older AI prompts ─────────────────────
  CIMENTACION:       { en: 'Foundation',             es: 'Cimentación',                  color: '#6B7280' },
  ESTRUCTURA:        { en: 'Framing',                es: 'Estructura',                   color: '#A16207' },
  TECHO:             { en: 'Roofing',                es: 'Techos',                       color: '#B91C1C' },
  ACABADOS:          { en: 'Finishes',               es: 'Acabados',                     color: '#22C55E' },
  AISLAMIENTO:       { en: 'Insulation',             es: 'Aislamiento',                  color: '#EC4899' },
  TABLAROCA:         { en: 'Drywall',                es: 'Tablaroca',                    color: '#D4A574' },
  FERRETERIA:        { en: 'Hardware',               es: 'Ferretería',                   color: '#64748B' },
}

// 19-category colors aligned with src/utils/takeoffCategoryColors.ts.
// Legacy aliases preserved for pre-migration DB rows.
const CATEGORY_COLORS: Record<string, string> = {
  // Canonical
  FOUNDATION: '#6B7280',
  FRAMING: '#A16207',
  EXTERIOR: '#059669',
  ROOFING: '#B91C1C',
  WINDOWS: '#0EA5E9',
  DOORS: '#7C3AED',
  PLUMBING_ROUGH: '#06B6D4',
  ELECTRICAL_ROUGH: '#F59E0B',
  HVAC: '#0D9488',
  INSULATION: '#EC4899',
  DRYWALL: '#D4A574',
  PLUMBING_FIXTURES: '#0891B2',
  LIGHTING_FANS: '#EAB308',
  PAINT: '#8B5CF6',
  CABINETS: '#EA580C',
  COUNTERTOPS: '#475569',
  FLOORING: '#78350F',
  APPLIANCES: '#4338CA',
  LANDSCAPING: '#65A30D',
  // Legacy aliases (pre-migration)
  SIDING: '#059669',
  WINDOWS_DOORS: '#0EA5E9',
  ELECTRICAL: '#F59E0B',
  ELECTRICAL_FINISH: '#EAB308',
  PLUMBING: '#06B6D4',
  PLUMBING_FINISH: '#0891B2',
  MEP_ROUGH: '#06B6D4',
  FINISHES: '#22C55E',
  HARDWARE: '#64748B',
  SITEWORK: '#92400E',
  MISCELLANEOUS: '#9E9890',
  // Mixed-case English (very old AI output)
  Foundation: '#6B7280', Framing: '#A16207', Roofing: '#B91C1C',
  Siding: '#059669', Windows: '#0EA5E9', Doors: '#7C3AED',
  Insulation: '#EC4899', Drywall: '#D4A574', Plumbing: '#06B6D4',
  Electrical: '#F59E0B', Finishes: '#22C55E', Sitework: '#92400E',
  Kitchen: '#009688', Bathroom: '#00BCD4',
}

const MATERIAL_ALTERNATIVES: Record<string, string[]> = {
  'Concrete Slab': ['Post-tension slab', 'Pier & beam', 'Stem wall + slab'],
  'Rebar': ['#4 Grade 60 Rebar', '#5 Grade 60 Rebar', 'Fiber mesh reinforcement', 'Welded wire fabric'],
  'Roof Shingles': ['OC Duration STORM (Class 4)', 'GAF Timberline HDZ', 'CertainTeed Landmark Pro', 'Metal standing seam'],
  'Underlayment': ['GAF FeltBuster Synthetic', 'Titanium UDL 30', '30# felt paper'],
  'Studs': ['2x4 SPF #2 Pre-cut', '2x4 SPF #1', '2x6 SPF #2 Pre-cut', 'Steel stud 3-5/8"'],
  'OSB Sheathing': ['7/16" OSB Exposure 1', '1/2" CDX Plywood', '15/32" Zip System'],
  'Insulation': ['R-13 Kraft-faced batts', 'R-15 Kraft-faced batts', 'R-19 unfaced batts', 'Open-cell spray foam', 'Closed-cell spray foam'],
  'Drywall': ['1/2" regular drywall', '5/8" Type X fire-rated', '1/2" moisture-resistant', '1/2" mold-resistant'],
  'PEX Tubing': ['PEX-A Uponor 3/4"', 'PEX-B SharkBite 3/4"', 'CPVC 3/4"', 'Copper Type L 3/4"'],
  'Electrical Wire': ['Romex 12/2 NM-B', 'Romex 14/2 NM-B', 'THHN in conduit', 'MC Cable 12/2'],
  'Siding': ['LP SmartSide Panel', 'James Hardie HardiePlank', 'Vinyl siding', 'Brick veneer'],
  'Vapor Barrier': ['Stego Wrap 15-mil', '10-mil poly sheeting', '6-mil poly sheeting'],
}

const WASTE_FACTORS: Record<string, { pct: number; label: string }> = {
  // Canonical 19-category set
  FOUNDATION:        { pct: 5,  label: 'Concrete overpour' },
  FRAMING:           { pct: 12, label: 'Lumber cuts & defects' },
  EXTERIOR:          { pct: 8,  label: 'Siding cuts & waste' },
  ROOFING:           { pct: 15, label: 'Hip/valley cuts & starter' },
  WINDOWS:           { pct: 0,  label: 'Units — no waste' },
  DOORS:             { pct: 0,  label: 'Units — no waste' },
  PLUMBING_ROUGH:    { pct: 5,  label: 'Pipe/fitting run waste' },
  ELECTRICAL_ROUGH:  { pct: 3,  label: 'Wire run waste' },
  HVAC:              { pct: 5,  label: 'Duct/line waste' },
  INSULATION:        { pct: 5,  label: 'Batts trimming' },
  DRYWALL:           { pct: 10, label: 'Drywall standard waste' },
  PLUMBING_FIXTURES: { pct: 0,  label: 'Fixtures — no waste' },
  LIGHTING_FANS:     { pct: 0,  label: 'Fixtures — no waste' },
  PAINT:             { pct: 10, label: 'Paint coverage waste' },
  CABINETS:          { pct: 0,  label: 'Pre-built — no waste' },
  COUNTERTOPS:       { pct: 8,  label: 'Slab cuts & seams' },
  FLOORING:          { pct: 10, label: 'Cuts & breakage' },
  APPLIANCES:        { pct: 0,  label: 'Units — no waste' },
  LANDSCAPING:       { pct: 5,  label: 'Plant/material waste' },
  // Legacy aliases
  SIDING:            { pct: 8,  label: 'Siding cuts & waste' },
  WINDOWS_DOORS:     { pct: 0,  label: 'Units — no waste' },
  ELECTRICAL:        { pct: 3,  label: 'Wire run waste' },
  ELECTRICAL_FINISH: { pct: 0,  label: 'Fixtures — no waste' },
  PLUMBING:          { pct: 5,  label: 'Pipe/fitting run waste' },
  PLUMBING_FINISH:   { pct: 0,  label: 'Fixtures — no waste' },
  MEP_ROUGH:         { pct: 5,  label: 'Pipe/wire run waste' },
  FINISHES:          { pct: 10, label: 'Tile/flooring cuts & breakage' },
  HARDWARE:          { pct: 3,  label: 'Minimal waste' },
  SITEWORK:          { pct: 5,  label: 'Fill/grade waste' },
}

// DFW 2025-2026 estimated retail prices by keyword
const DFW_PRICES: [RegExp, number][] = [
  [/2x4.*stud|2x4.*spf|2x4.*pre-?cut/i, 4.50], [/2x4x1[06]/i, 6.50], [/2x6/i, 7.50], [/2x8/i, 9.50], [/2x10/i, 13.00], [/2x12/i, 16.00],
  [/osb.*7\/16|osb.*sheathing/i, 15.50], [/osb.*1\/2|cdx.*plywood/i, 17.00], [/zip.*system/i, 24.00],
  [/shingle|duration|timberline|landmark/i, 110.00], [/underlayment|feltbust/i, 65.00], [/drip.*edge/i, 1.20], [/ridge.*vent/i, 3.50],
  [/r-?13.*batt|r-?13.*fiberglass/i, 0.80], [/r-?19/i, 1.10], [/r-?38.*blown/i, 1.50], [/spray.*foam/i, 2.80],
  [/romex.*12\/2|nm-?b.*12/i, 0.55], [/romex.*14\/2|nm-?b.*14/i, 0.42], [/romex.*10/i, 1.10],
  [/breaker.*panel|200.*panel/i, 185.00], [/breaker.*20|breaker.*15/i, 6.50], [/gfi.*outlet|gfci/i, 18.00], [/outlet|receptacle/i, 1.50], [/switch/i, 2.50],
  [/recessed.*light|can.*light/i, 12.00], [/ceiling.*fan/i, 120.00], [/vanity.*light/i, 65.00], [/pendant/i, 85.00],
  [/pex.*3\/4/i, 0.85], [/pex.*1\/2/i, 0.65], [/pvc.*dwv.*3/i, 4.20], [/pvc.*dwv.*2|pvc.*1-?1\/2/i, 2.80],
  [/toilet/i, 220.00], [/faucet.*kitchen|pull.*down.*faucet/i, 180.00], [/faucet.*bath/i, 95.00], [/sink.*kitchen/i, 250.00], [/sink.*bath/i, 120.00],
  [/shower.*pan/i, 180.00], [/shower.*head/i, 45.00], [/shower.*valve/i, 85.00], [/water.*heater/i, 850.00], [/garbage.*disposal/i, 120.00],
  [/drywall.*1\/2.*4x12|gypsum.*1\/2/i, 14.50], [/drywall.*5\/8|type.*x/i, 16.80], [/joint.*compound/i, 12.00], [/drywall.*screw/i, 8.00],
  [/concrete.*slab|concrete.*3[,.]?[05]00/i, 165.00], [/rebar.*#4/i, 0.75], [/rebar.*#5/i, 1.10],
  [/hardie.*plank|fiber.*cement/i, 1.80], [/vinyl.*siding/i, 1.20], [/brick/i, 8.50], [/tyvek|housewrap/i, 0.35],
  [/lvp|vinyl.*plank/i, 3.50], [/porcelain.*tile/i, 4.50], [/carpet/i, 2.80], [/hardwood|engineered.*oak/i, 8.50],
  [/granite.*counter/i, 55.00], [/quartz.*counter/i, 75.00], [/cabinet/i, 180.00],
  [/simpson.*ltp|tie.*plate|hurricane/i, 3.50], [/simpson.*hdu|hold.*down/i, 22.00], [/anchor.*bolt/i, 2.50],
  [/nail.*16d|framing.*nail/i, 12.00], [/nail.*8d/i, 10.00], [/screw/i, 8.00],
  [/window|low-?e/i, 250.00], [/entry.*door/i, 350.00], [/interior.*door/i, 85.00], [/garage.*door/i, 850.00],
  [/paint|sherwin|superpaint/i, 55.00], [/primer/i, 35.00], [/caulk/i, 5.50],
  [/stego.*wrap|vapor.*barrier/i, 0.45], [/post.*tension|pt.*cable/i, 18.00],
  [/lvl.*beam|microllam/i, 6.50], [/truss/i, 85.00],
]

function getEstimatedPrice(name: string): number | null {
  const lower = name.toLowerCase()
  for (const [regex, price] of DFW_PRICES) {
    if (regex.test(lower)) return price
  }
  return null
}

function findAlternatives(itemName: string): string[] {
  for (const [key, alts] of Object.entries(MATERIAL_ALTERNATIVES)) {
    if (itemName.toLowerCase().includes(key.toLowerCase())) return alts
  }
  return []
}

// ── DEMO DATA ───────────────────────────────────


// DEMO TAKEOFF — Single, correct instance (bilingual)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const DEMO_TAKEOFF = {
  id: 'demo-takeoff-1',
  status: 'NEEDS_REVIEW' as const,
  totalItems: 27,
  processingMs: 4200,
  createdAt: new Date().toISOString(),
  heights: {
    gradeToRidge: "28'-10 3/8\"",
    floor1ToCeiling: "9'-0\"",
    floor2ToCeiling: "8'-0\"",
    floor1ToFloor2: "10'-0\"",
  },
  items: [
    { id: '1', categoryId: 'FOUNDATION', name: 'Concrete Slab 4" 3,500 PSI', quantity: 22, unit: 'CY', unitPrice: 165, approved: true },
    { id: '3', categoryId: 'FRAMING', name: '2x4 SPF #2 104-5/8" Pre-cut Stud', quantity: 520, unit: 'EA', unitPrice: 4.85, approved: false },
    { id: '4', categoryId: 'FRAMING', name: '2x10 SPF #2 Floor Joist', quantity: 180, unit: 'EA', unitPrice: 12.50, approved: false },
    { id: '6', categoryId: 'ROOFING', name: 'Composition Shingles 30-Year', quantity: 26, unit: 'SQ', unitPrice: 125, approved: false },
    { id: '8', categoryId: 'ELECTRICAL_ROUGH', name: 'Romex 12/2 NM-B Wire', quantity: 1500, unit: 'LF', unitPrice: 0.65, approved: false },
    { id: '9', categoryId: 'ELECTRICAL_ROUGH', name: 'Plastic Box 1-Gang', quantity: 85, unit: 'EA', unitPrice: 0.80, approved: false },
    { id: '10', categoryId: 'PLUMBING_ROUGH', name: 'PEX-A Tubing ¾"', quantity: 350, unit: 'LF', unitPrice: 1.25, approved: false },
    { id: '11', categoryId: 'PLUMBING_ROUGH', name: 'ABS Drain Pipe 3"', quantity: 250, unit: 'LF', unitPrice: 3.85, approved: false },
    { id: '12', categoryId: 'HVAC', name: '4-Ton Heat Pump 16 SEER2', quantity: 1, unit: 'LOT', unitPrice: 3200, approved: false },
    { id: '13', categoryId: 'INSULATION', name: 'Fiberglass Batts R-13', quantity: 2800, unit: 'SF', unitPrice: 0.55, approved: false },
    { id: '14', categoryId: 'DRYWALL', name: '½" Gypsum Board (4x8)', quantity: 185, unit: 'SHEET', unitPrice: 12.50, approved: false },
    { id: '15', categoryId: 'DRYWALL', name: 'Joint Compound', quantity: 85, unit: 'GAL', unitPrice: 18.50, approved: false },
    { id: '16', categoryId: 'ELECTRICAL_FINISH', name: 'Switch & Outlet Covers', quantity: 50, unit: 'EA', unitPrice: 2.50, approved: false },
    { id: '17', categoryId: 'ELECTRICAL_FINISH', name: 'Light Fixtures', quantity: 35, unit: 'EA', unitPrice: 45.00, approved: false },
    { id: '18', categoryId: 'PLUMBING_FINISH', name: 'Faucets & Fixtures', quantity: 8, unit: 'EA', unitPrice: 150.00, approved: false },
    { id: '19', categoryId: 'PLUMBING_FINISH', name: 'Trim & Drains', quantity: 1, unit: 'LOT', unitPrice: 800, approved: false },
    { id: '20', categoryId: 'SIDING', name: 'HardiePlank Lap Siding 8.25"', quantity: 600, unit: 'SF', unitPrice: 2.85, approved: false },
    { id: '21', categoryId: 'WINDOWS_DOORS', name: 'Vinyl Single-Hung 3\'-0" x 5\'-0"', quantity: 8, unit: 'EA', unitPrice: 285, approved: false },
    { id: '22', categoryId: 'FLOORING', name: 'Luxury Vinyl Plank', quantity: 1400, unit: 'SF', unitPrice: 3.50, approved: false },
    { id: '23', categoryId: 'PAINT', name: 'Interior Latex Paint Eggshell', quantity: 8900, unit: 'SF', unitPrice: 0.45, approved: false },
    { id: '24', categoryId: 'CABINETS', name: 'Maple Shaker Base Cabinet', quantity: 8, unit: 'EA', unitPrice: 425, approved: false },
    { id: '25', categoryId: 'COUNTERTOPS', name: '3cm Granite Countertop', quantity: 45, unit: 'SF', unitPrice: 125, approved: false },
    { id: '26', categoryId: 'APPLIANCES', name: 'Stainless Range Gas', quantity: 1, unit: 'EA', unitPrice: 1200, approved: false },
    { id: '27', categoryId: 'LANDSCAPING', name: '4" Concrete Driveway', quantity: 8, unit: 'CY', unitPrice: 165, approved: false },
    { id: '28', categoryId: 'MISCELLANEOUS', name: 'General Contractor Labor', quantity: 1, unit: 'LOT', unitPrice: 8500, approved: false },
  ],
}


// ── MISSING-ADDRESS WARNING BANNER ────────────────
// Shown on the takeoff page when the project has no city set. The AI will fall
// back to DFW defaults; the builder can add the address inline and re-run takeoffs.

function MissingAddressBanner({
  projectId,
  project,
  isEs,
  getToken,
}: {
  projectId: string
  project: { address: string | null; city: string | null; state: string | null; zipCode: string | null }
  isEs: boolean
  getToken: () => Promise<string | null>
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    address: project.address || '',
    city: project.city || '',
    state: project.state || 'TX',
    zipCode: project.zipCode || '',
  })
  const isLocationComplete = Boolean(form.city.trim() && form.state.trim() && form.zipCode.trim())

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error('Save failed')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setEditing(false)
    },
  })

  return (
    <div className="border border-[#F5A623]/40 bg-[#FFF8EC] rounded-xl p-3">
      <div className="flex items-start gap-2">
        <MapPin size={16} className="text-[#F5A623] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-[800] text-[#1C1A17]">
            {isEs ? 'Falta ubicación para el takeoff' : 'Takeoff location required'}
          </p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? 'Antes de subir planos, guarda ciudad, estado y zip para que el takeoff use la jurisdicción correcta.'
              : 'Before uploading plans, save city, state, and zip so takeoff uses the correct jurisdiction.'}
          </p>

          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="mt-2 text-[10px] font-[800] text-[#06539B] hover:text-[#14457E] underline"
            >
              {isEs ? 'Agregar dirección →' : 'Add address →'}
            </button>
          ) : (
            <div className="mt-2 space-y-2">
              <input
                placeholder={isEs ? 'Calle (ej: 4631 Spring Garden Dr)' : 'Street (e.g. 4631 Spring Garden Dr)'}
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                className="w-full border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[11px] font-[600] focus:outline-none focus:border-[#06539B] bg-white"
              />
              <div className="grid grid-cols-5 gap-2">
                <input
                  placeholder={isEs ? 'Ciudad' : 'City'}
                  value={form.city}
                  onChange={e => setForm({ ...form, city: e.target.value })}
                  className="col-span-3 border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[11px] font-[600] focus:outline-none focus:border-[#06539B] bg-white"
                />
                <input
                  placeholder="TX"
                  value={form.state}
                  onChange={e => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className="col-span-1 border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[11px] font-[600] focus:outline-none focus:border-[#06539B] bg-white"
                />
                <input
                  placeholder="75201"
                  value={form.zipCode}
                  onChange={e => setForm({ ...form, zipCode: e.target.value })}
                  maxLength={10}
                  className="col-span-1 border border-[#ECEAE4] rounded-lg px-2 py-1.5 text-[11px] font-[600] focus:outline-none focus:border-[#06539B] bg-white"
                />
              </div>
              {saveMutation.isError && (
                <p className="text-[10px] text-red-600 font-[600]">
                  {isEs ? 'No se pudo guardar. Intenta de nuevo.' : 'Save failed. Try again.'}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="text-[10px] font-[800] text-[#9E9890] hover:text-[#1C1A17] px-2 py-1"
                >
                  {isEs ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={!isLocationComplete || saveMutation.isPending}
                  className="text-[10px] font-[800] text-white bg-[#06539B] hover:bg-[#14457E] disabled:opacity-40 rounded-full px-3 py-1 transition-colors"
                >
                  {saveMutation.isPending ? '...' : isEs ? 'Guardar' : 'Save'}
                </button>
              </div>
              {!isLocationComplete && (
                <p className="text-[10px] font-[600] text-[#9E9890]">
                  {isEs ? 'Se requiere ciudad, estado y zip.' : 'City, state, and zip are required.'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── COMPONENT ─────────────────────────────────────

export default function TakeoffModule() {
  const { getToken } = useAuth()
  const params = useParams()
  // projectId may be in params directly or need extraction from URL
  // (descendant <Routes> in ProjectLayout doesn't forward parent params in all RR versions)
  const projectId = params.projectId || window.location.pathname.split('/projects/')[1]?.split('/')[0]
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const isEs = i18n.language === 'es'

  const [uploading, setUploading] = useState(false)
  const [processingTakeoffId, setProcessingTakeoffId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [activeTakeoffIdx, setActiveTakeoffIdx] = useState(0)
  const [chunkProgress, setChunkProgress] = useState<{ completed: number; total: number } | null>(null)
  const [takeoffWarnings, setTakeoffWarnings] = useState<string[] | null>(null)

  const showShadow = new URLSearchParams(window.location.search).has('shadow')
  const isDemo = isDemoProject(projectId)

  // Retry transient 5xx errors (Vercel proxy → Railway can intermittently 502/503).
  // Only retry server errors; 4xx are not transient.
  const retryOn5xx = (failureCount: number, error: unknown) => {
    if (failureCount >= 2) return false
    const msg = (error as Error)?.message || ''
    return msg.includes('503') || msg.includes('502') || msg.includes('unavailable')
  }

  // Legacy containment posture. Legacy takeoff has produced duplicated
  // quantities; until source-backed evidence gates mature, this engine
  // is reference-only. Existing visible records remain readable.
  const v3cfg = getDiagnosticV3Config()
  const legacyApprovalAllowed = v3cfg.TAKEOFF_LEGACY_QUANTITY_APPROVAL_ENABLED // typed false
  const legacyCustomerOutputsAllowed = v3cfg.TAKEOFF_LEGACY_CUSTOMER_OUTPUTS_ENABLED // typed false
  const showDiagnosticV3Link = isDiagnosticV3VisibleToClient()

  const { data: apiTakeoffs, isLoading } = useQuery<Takeoff[]>({
    queryKey: ['takeoffs', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/takeoff`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error(`API unavailable ${r.status}`)
      return r.json()
    }) },
    retry: retryOn5xx,
    retryDelay: 2000,
    enabled: !isDemo && !!projectId,
  })

  // Project address — used to show jurisdiction warning banner when missing
  const { data: project } = useQuery<{ id: string; name: string; address: string | null; city: string | null; state: string | null; zipCode: string | null }>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
      if (!res.ok) throw new Error(`API unavailable ${res.status}`)
      return res.json()
    },
    retry: retryOn5xx,
    retryDelay: 2000,
    enabled: !isDemo && !!projectId,
  })

  const { data: shadowProjectSummaryApi } = useQuery<ProjectShadowSummary>({
    queryKey: ['takeoff-shadow-summary', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/takeoff/shadow-summary`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error(`Shadow summary unavailable ${res.status}`)
      return res.json()
    },
    retry: retryOn5xx,
    retryDelay: 2000,
    enabled: !isDemo && !!projectId,
  })

  // Demo projects: always show demo data. Real projects: only show API data.
  // Hide ERROR takeoffs from the UI — users should only see NEEDS_REVIEW / CONFIRMED / APPROVED.
  const takeoffs = isDemo
    ? ([DEMO_TAKEOFF] as any)
    : (Array.isArray(apiTakeoffs) ? apiTakeoffs.filter((t: any) => t.status !== 'ERROR') : [])

  // Poll takeoff status while AI is processing
  useEffect(() => {
    if (!processingTakeoffId) return
    let cancelled = false
    let consecutiveFailures = 0
    const poll = async () => {
      try {
        const token = await getToken()
        const res = await apiFetch(`/api/projects/${projectId}/takeoff/${processingTakeoffId}/status`, {
          headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
        })
        if (cancelled) return
        if (!res.ok) {
          consecutiveFailures++
          // Vercel proxy intermittently 503s — retry silently up to 6 times (30s)
          // before showing an error to the user
          if (consecutiveFailures >= 6) {
            setUploadError(isEs ? 'Conexión intermitente — reintentando...' : 'Intermittent connection — retrying...')
          }
          return
        }
        consecutiveFailures = 0
        setUploadError(null)
        const data = await res.json()
        if (cancelled) return
        if (data.chunksCompleted != null && data.totalChunks != null) {
          setChunkProgress({ completed: data.chunksCompleted, total: data.totalChunks })
        }
        if (data.status === 'NEEDS_REVIEW' || data.status === 'CONFIRMED' || data.status === 'APPROVED') {
          setProcessingTakeoffId(null)
          setUploading(false)
          setChunkProgress(null)
          if (data.warnings) setTakeoffWarnings(data.warnings)
          queryClient.invalidateQueries({ queryKey: ['takeoffs', projectId] })
          queryClient.invalidateQueries({ queryKey: ['takeoff-shadow-summary', projectId] })
        } else if (data.status === 'ERROR') {
          setProcessingTakeoffId(null)
          setUploading(false)
          setChunkProgress(null)
          setUploadError('AI analysis failed — please try again')
          queryClient.invalidateQueries({ queryKey: ['takeoffs', projectId] })
        }
      } catch { /* network error — keep polling */ }
    }
    const interval = setInterval(poll, 5000)
    poll() // immediate first check
    return () => { cancelled = true; clearInterval(interval) }
  }, [processingTakeoffId, projectId, getToken, queryClient])

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken()
      setUploading(true)
      setUploadError(null)
      const formData = new FormData()
      formData.append('file', file)
      const res = await apiFetch(`/api/projects/${projectId}/takeoff`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: formData
      })
      if (!res.ok) {
        const payload = await res.json().catch(() => null)
        throw new Error(payload?.error || `Upload failed: ${res.status}`)
      }
      return res.json()
    },
    onSuccess: (data: any) => {
      if (data.status === 'AI_PROCESSING' && data.takeoffId) {
        // Async mode — start polling
        setProcessingTakeoffId(data.takeoffId)
      } else {
        // Sync response (legacy) — done immediately
        queryClient.invalidateQueries({ queryKey: ['takeoffs', projectId] })
        queryClient.invalidateQueries({ queryKey: ['takeoff-shadow-summary', projectId] })
        setUploading(false)
      }
    },
    onError: (err: Error) => {
      setUploading(false)
      setUploadError(err.message || 'Upload failed')
    }
  })

  const confirmMutation = useMutation({
    mutationFn: async (takeoffId: string) => { const token = await getToken(); return apiFetch(`/api/takeoffs/${takeoffId}/confirm`, { method: 'PATCH', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takeoffs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['takeoff-shadow-summary', projectId] })
    }
  })

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!isTakeoffLocationComplete) {
      setUploadError(isEs ? 'Guarda ciudad, estado y zip antes de subir planos.' : 'Save city, state, and zip before uploading plans.')
      e.target.value = ''
      return
    }
    if (file) uploadMutation.mutate(file)
  }

  const toggleCategory = (cat: string) => {
    const next = new Set(expandedCategories)
    next.has(cat) ? next.delete(cat) : next.add(cat)
    setExpandedCategories(next)
  }

  const activeTakeoff = takeoffs?.[activeTakeoffIdx] || takeoffs?.[0]
  const isTakeoffLocationComplete = isDemo || Boolean(project?.city?.trim() && project?.state?.trim() && project?.zipCode?.trim())
  const shadowProjectSummary = shadowProjectSummaryApi || {
    totalCompared: 0,
    latest: null,
    rows: [],
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {t('Takeoff Analysis')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {t('Upload plans, AI calculates materials')}
          </p>
        </div>
      </div>

      {/* Legacy containment notice — keeps legacy takeoff visible for
          reference only. Existing visible records remain readable.
          Uses existing design tokens (#ECEAE4 border, #FAFAF8 bg,
          #9E9890 muted text, #1C1A17 strong text). Rendered above the
          MissingAddressBanner because the containment context applies
          regardless of jurisdiction state. */}
      {!legacyCustomerOutputsAllowed && (
        <div
          role="status"
          className="bg-[#FAFAF8] border border-[#ECEAE4] rounded-xl p-3 flex items-start gap-2"
        >
          <Info size={14} className="text-[#9E9890] mt-[2px] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[11px] font-[700] text-[#1C1A17]">
              {t(
                'Takeoff is being upgraded to a source-backed diagnostic workflow. Existing legacy quantities are preserved for reference only and require manual review.',
              )}
            </p>
            {showDiagnosticV3Link && projectId && (
              <a
                href={`/projects/${projectId}/takeoff-diagnostic`}
                className="inline-flex items-center gap-1.5 mt-2 text-[10px] font-[800] text-[#1C1A17] underline underline-offset-2 hover:text-[#9E9890]"
              >
                <FlaskConical size={11} />
                {t('Open Diagnostic Takeoff (v3) — developer only')}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Missing address warning — shown when the project has no city set.
          Rendered ABOVE the upload area so the builder fixes jurisdiction context
          before uploading plans. */}
      {!isDemo && projectId && project && !isTakeoffLocationComplete && (
        <MissingAddressBanner
          projectId={projectId}
          project={project}
          isEs={isEs}
          getToken={getToken}
        />
      )}

      {/* Upload zone */}
      <div
        onClick={() => {
          if (!isTakeoffLocationComplete || uploading) return
          fileRef.current?.click()
        }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isTakeoffLocationComplete
            ? 'border-[#ECEAE4] cursor-pointer hover:border-[#06539B] hover:bg-[#FAFAF8]'
            : 'border-[#F5A623]/40 bg-[#FFF8EC] cursor-not-allowed'
        }`}
      >
        <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={handleFile} className="hidden" disabled={!isTakeoffLocationComplete || uploading} />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            {processingTakeoffId && chunkProgress && chunkProgress.total > 1 ? (
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#ECEAE4" strokeWidth="4" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#06539B" strokeWidth="4"
                    strokeDasharray={`${(chunkProgress.completed / chunkProgress.total) * 175.9} 175.9`}
                    strokeLinecap="round" className="transition-all duration-700" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[13px] font-[800] text-[#06539B]">
                  {chunkProgress.completed}/{chunkProgress.total}
                </span>
              </div>
            ) : (
              <Loader2 size={32} className="text-[#06539B] animate-spin" />
            )}
            <span className="text-[12px] font-[700] text-[#1C1A17]">
              {processingTakeoffId
                ? chunkProgress
                  ? chunkProgress.completed === 0
                    ? isEs
                      ? `Procesando ${chunkProgress.total} lotes en paralelo...`
                      : `Processing ${chunkProgress.total} batches in parallel...`
                    : isEs
                      ? `Lote ${chunkProgress.completed} de ${chunkProgress.total} listo...`
                      : `Batch ${chunkProgress.completed} of ${chunkProgress.total} done...`
                  : t('Preparing AI analysis...')
                : t('Uploading file...')}
            </span>
            <span className="text-[10px] font-[600] text-[#9E9890]">
              {processingTakeoffId ? t('Do not close or leave this page during analysis') : t('Please wait...')}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload size={32} className={isTakeoffLocationComplete ? 'text-[#9E9890]' : 'text-[#F5A623]'} />
            <span className="text-[12px] font-[700] text-[#1C1A17]">
              {isTakeoffLocationComplete
                ? t('Upload architectural plan')
                : isEs ? 'Completa la ubicación primero' : 'Complete location first'}
            </span>
            <span className="text-[10px] font-[600] text-[#9E9890]">
              {isTakeoffLocationComplete
                ? 'PDF, JPG, PNG — max 50MB'
                : isEs ? 'Se requiere ciudad, estado y zip antes del análisis' : 'City, state, and zip are required before analysis'}
            </span>
          </div>
        )}
        {uploadError && (
          <p className="text-[11px] text-red-600 font-[600] mt-2">{uploadError}</p>
        )}
      </div>

      {/* Partial results warning */}
      {takeoffWarnings && takeoffWarnings.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[12px] font-[800] text-amber-700">{t('Partial results')}</p>
            <p className="text-[11px] font-[600] text-amber-600 mt-0.5">
              {takeoffWarnings.map((w, i) => <span key={i}>{w}<br /></span>)}
              {t('Results below are from the completed batches only.')}
            </p>
            <button onClick={() => setTakeoffWarnings(null)} className="text-[10px] font-[700] text-amber-500 mt-1 underline">{t('Dismiss')}</button>
          </div>
        </div>
      )}

      {showShadow && shadowProjectSummary.totalCompared > 0 && (
        <div className="border border-[#BFDBFE] bg-[#F8FBFF] rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-[12px] font-[800] text-[#1E3A8A]">
                {isEs ? 'Resumen Shadow por Proyecto' : 'Project Shadow Summary'}
              </h3>
              <p className="text-[10px] font-[600] text-[#4B5563] mt-0.5">
                {isEs
                  ? `${shadowProjectSummary.totalCompared} takeoffs ya tienen comparación v1 vs shadow.`
                  : `${shadowProjectSummary.totalCompared} takeoffs already have v1 vs shadow comparison.`}
              </p>
            </div>
            {shadowProjectSummary.latest && (
              <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-white text-[#06539B] border border-[#BFDBFE]">
                {isEs ? 'Más reciente' : 'Latest'}
              </span>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Takeoffs comparados' : 'Compared takeoffs'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowProjectSummary.totalCompared}</div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta 1/2" actual' : 'Current 1/2" delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaRegular >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaRegular}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta Type X actual' : 'Current Type X delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaTypeX >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaTypeX}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Shadow BOM actual' : 'Current shadow BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta HVAC actual' : 'Current HVAC delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaHvacSystems >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaHvacSystems}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM HVAC actual' : 'Current HVAC BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowHvacBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta luz actual' : 'Current lighting delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaLightingFixtureCount >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaLightingFixtureCount}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM luz actual' : 'Current lighting BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowLightingBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta gabinetes actual' : 'Current cabinet delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaCabinetUnits >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaCabinetUnits}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM gabinetes actual' : 'Current cabinets BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowCabinetBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta cubiertas actual' : 'Current countertops delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaCountertopSf >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaCountertopSf}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM cubiertas actual' : 'Current countertops BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowCountertopBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta pisos actual' : 'Current flooring delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaFlooringSf >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaFlooringSf}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM pisos actual' : 'Current flooring BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowFlooringBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta pintura actual' : 'Current paint delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaPaintAreaSf >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaPaintAreaSf}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM pintura actual' : 'Current paint BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowPaintBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta appliances actual' : 'Current appliance delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaApplianceUnits >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaApplianceUnits}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM appliances actual' : 'Current appliances BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowApplianceBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta fixtures actual' : 'Current fixtures delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaPlumbingFixtureCount >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaPlumbingFixtureCount}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM fixtures actual' : 'Current fixtures BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowPlumbingFixtureBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta plomería actual' : 'Current plumbing delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaPlumbingSupplyLf >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaPlumbingSupplyLf}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM plomería actual' : 'Current plumbing BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowPlumbingBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta cableado actual' : 'Current electrical wire delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaElectricalWireLf >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaElectricalWireLf}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM eléctrico actual' : 'Current electrical BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowElectricalBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta techo actual' : 'Current roofing delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaRoofingSquares >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaRoofingSquares}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM techo actual' : 'Current roofing BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowRoofingBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta aislante muro' : 'Current wall insulation delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaInsulationWall >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaInsulationWall}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM aislante actual' : 'Current insulation BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowInsulationBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta exterior actual' : 'Current exterior delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaExteriorCladding >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaExteriorCladding}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM exterior actual' : 'Current exterior BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowExteriorBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta puertas actual' : 'Current door delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaDoorUnits >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaDoorUnits}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM puertas actual' : 'Current doors BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowDoorBomItemCount ?? '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Delta ventanas actual' : 'Current window delta'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest ? `${shadowProjectSummary.latest.deltaWindowUnits >= 0 ? '+' : ''}${shadowProjectSummary.latest.deltaWindowUnits}` : '—'}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
              <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM ventanas actual' : 'Current windows BOM'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">
                {shadowProjectSummary.latest?.shadowWindowBomItemCount ?? '—'}
              </div>
            </div>
          </div>

          {shadowProjectSummary.rows.length > 1 && (
            <div className="mt-3 rounded-lg border border-[#DBEAFE] bg-white overflow-hidden">
              <div className="px-3 py-2 bg-[#F8FBFF] border-b border-[#DBEAFE] text-[10px] font-[800] text-[#4B5563] uppercase tracking-[0.35px]">
                {isEs ? 'Historial rápido' : 'Quick history'}
              </div>
              <div className="divide-y divide-[#F2F0EB]">
                {shadowProjectSummary.rows.slice(0, 4).map((row, idx) => (
                  <div key={row.takeoffId} className="px-3 py-2 flex items-center gap-3 text-[10px]">
                    <span className="font-[800] text-[#06539B]">#{shadowProjectSummary.rows.length - idx}</span>
                    <span className="font-[700] text-[#6B7280]">{new Date(row.createdAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}</span>
                    <span className="font-[700] text-[#1C1A17]">{isEs ? '1/2"' : '1/2"'} {row.deltaRegular >= 0 ? '+' : ''}{row.deltaRegular}</span>
                    <span className="font-[700] text-[#1C1A17]">Type X {row.deltaTypeX >= 0 ? '+' : ''}{row.deltaTypeX}</span>
                    <span className="font-[700] text-[#1C1A17]">CB {row.deltaCornerBead >= 0 ? '+' : ''}{row.deltaCornerBead}</span>
                    <span className="font-[700] text-[#1C1A17]">HVAC {row.deltaHvacSystems >= 0 ? '+' : ''}{row.deltaHvacSystems}</span>
                    <span className="font-[700] text-[#1C1A17]">LGT {row.deltaLightingFixtureCount >= 0 ? '+' : ''}{row.deltaLightingFixtureCount}</span>
                    <span className="font-[700] text-[#1C1A17]">CAB {row.deltaCabinetUnits >= 0 ? '+' : ''}{row.deltaCabinetUnits}</span>
                    <span className="font-[700] text-[#1C1A17]">TOP {row.deltaCountertopSf >= 0 ? '+' : ''}{row.deltaCountertopSf}</span>
                    <span className="font-[700] text-[#1C1A17]">FLR {row.deltaFlooringSf >= 0 ? '+' : ''}{row.deltaFlooringSf}</span>
                    <span className="font-[700] text-[#1C1A17]">PNT {row.deltaPaintAreaSf >= 0 ? '+' : ''}{row.deltaPaintAreaSf}</span>
                    <span className="font-[700] text-[#1C1A17]">APP {row.deltaApplianceUnits >= 0 ? '+' : ''}{row.deltaApplianceUnits}</span>
                    <span className="font-[700] text-[#1C1A17]">FX {row.deltaPlumbingFixtureCount >= 0 ? '+' : ''}{row.deltaPlumbingFixtureCount}</span>
                    <span className="font-[700] text-[#1C1A17]">PLMB {row.deltaPlumbingSupplyLf >= 0 ? '+' : ''}{row.deltaPlumbingSupplyLf}</span>
                    <span className="font-[700] text-[#1C1A17]">ELEC {row.deltaElectricalWireLf >= 0 ? '+' : ''}{row.deltaElectricalWireLf}</span>
                    <span className="font-[700] text-[#1C1A17]">ROOF {row.deltaRoofingSquares >= 0 ? '+' : ''}{row.deltaRoofingSquares}</span>
                    <span className="font-[700] text-[#1C1A17]">INS {row.deltaInsulationWall >= 0 ? '+' : ''}{row.deltaInsulationWall}</span>
                    <span className="font-[700] text-[#1C1A17]">{isEs ? 'EXT' : 'EXT'} {row.deltaExteriorCladding >= 0 ? '+' : ''}{row.deltaExteriorCladding}</span>
                    <span className="font-[700] text-[#1C1A17]">{isEs ? 'DR' : 'DR'} {row.deltaDoorUnits >= 0 ? '+' : ''}{row.deltaDoorUnits}</span>
                    <span className="font-[700] text-[#1C1A17]">{isEs ? 'WIN' : 'WIN'} {row.deltaWindowUnits >= 0 ? '+' : ''}{row.deltaWindowUnits}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error state for failed takeoffs */}
      {activeTakeoff && (activeTakeoff.status === 'ERROR' || activeTakeoff.status === 'FAILED') && (
        <div className="border border-red-200 bg-red-50 rounded-xl p-5 text-center">
          <AlertCircle size={28} className="text-red-400 mx-auto mb-2" />
          <p className="text-[13px] font-[800] text-red-700 mb-1">{t('Analysis failed')}</p>
          <p className="text-[11px] font-[600] text-red-500">
            {activeTakeoff.rawAiResponse?.error?.includes('Unsupported file type')
              ? t('Unsupported file format. Use PDF, PNG, or JPG.')
              : activeTakeoff.rawAiResponse?.error?.includes('incomplete')
              ? t('Upload plans first — the AI needs architectural drawings to extract materials')
              : t('Upload your plans again. If the error persists, try a higher-resolution PDF.')}
          </p>
        </div>
      )}

      {/* Active takeoff results */}
      {activeTakeoff && activeTakeoff.status !== 'ERROR' && activeTakeoff.status !== 'FAILED' && (
        <TakeoffResults
          takeoff={activeTakeoff}
          isEs={isEs}
          projectId={projectId!}
          getToken={getToken}
          expandedCategories={expandedCategories}
          onToggleCategory={toggleCategory}
          legacyApprovalAllowed={legacyApprovalAllowed}
          onConfirm={() => confirmMutation.mutate(activeTakeoff.id)}
          isConfirming={confirmMutation.isPending}
        />
      )}

      {/* All takeoffs */}
      {takeoffs && takeoffs.length > 1 && (
        <div>
          <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
            {t('All Analyses')}
          </h3>
          <div className="space-y-2">
            {takeoffs.map((tk: typeof takeoffs[0], idx: number) => {
              const isActive = idx === activeTakeoffIdx
              return (
                <div
                  key={tk.id}
                  onClick={() => { setActiveTakeoffIdx(idx); setExpandedCategories(new Set()) }}
                  className={`flex items-center gap-3 p-3 border rounded-xl group cursor-pointer transition-colors ${isActive ? 'bg-[#F0F7FF] border-[#2196F3]' : 'bg-white border-[#ECEAE4] hover:bg-gray-50'}`}
                >
                  <span className={`text-[11px] font-[800] w-6 text-center ${isActive ? 'text-[#2196F3]' : 'text-[#9E9890]'}`}>#{idx + 1}</span>
                  <FileText size={16} className={isActive ? 'text-[#2196F3]' : 'text-[#9E9890]'} />
                  <span className="text-[11px] font-[700] flex-1">{tk.totalItems || tk.items?.length || 0} {t('items')}</span>
                  <StatusBadge status={tk.status} />
                  <span className="text-[9px] font-[600] text-[#9E9890]">
                    {new Date(tk.createdAt).toLocaleDateString()}
                  </span>
                  {idx > 0 && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          const token = await getToken()
                          await apiFetch(`/api/projects/${projectId}/takeoff/${tk.id}`, {
                            method: 'DELETE',
                            headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
                          })
                          if (activeTakeoffIdx >= idx) setActiveTakeoffIdx(Math.max(0, activeTakeoffIdx - 1))
                          queryClient.invalidateQueries({ queryKey: ['takeoffs', projectId] })
                        } catch { /* endpoint may not exist */ }
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      title={t('Delete analysis')}
                    >
                      <Trash2 size={14} className="text-gray-300 hover:text-red-500 transition-colors" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── TAKEOFF RESULTS ───────────────────────────────

function TakeoffResults({ takeoff, isEs, projectId, getToken, expandedCategories, onToggleCategory, onConfirm, isConfirming, legacyApprovalAllowed }: {
  takeoff: Takeoff
  isEs: boolean
  projectId: string
  getToken: () => Promise<string | null>
  expandedCategories: Set<string>
  onToggleCategory: (cat: string) => void
  onConfirm: () => void
  isConfirming: boolean
  legacyApprovalAllowed: boolean
}) {
  const { t } = useTranslation()
  const showShadow = new URLSearchParams(window.location.search).has('shadow')
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set())
  const [approvedItems, setApprovedItems] = useState<Set<string>>(new Set())
  const [overrideStatus, setOverrideStatus] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [editedPrices, setEditedPrices] = useState<Record<string, number>>({})
  const [editedNames, setEditedNames] = useState<Record<string, string>>({})
  const [openAltDropdown, setOpenAltDropdown] = useState<string | null>(null)
  const [priceCompareId, setPriceCompareId] = useState<string | null>(null)
  const shadow = takeoff.rawAiResponse?.shadow
  const shadowExterior = shadow?.result?.systems?.exterior
  const shadowExteriorComparison = shadow?.comparison?.exterior
  const shadowRoofing = shadow?.result?.systems?.roofing
  const shadowRoofingComparison = shadow?.comparison?.roofing
  const shadowHvac = shadow?.result?.systems?.hvac
  const shadowHvacComparison = shadow?.comparison?.hvac
  const shadowLightingFans = shadow?.result?.systems?.lightingFans
  const shadowLightingFansComparison = shadow?.comparison?.lightingFans
  const shadowCabinets = shadow?.result?.systems?.cabinets
  const shadowCabinetsComparison = shadow?.comparison?.cabinets
  const shadowCountertops = shadow?.result?.systems?.countertops
  const shadowCountertopsComparison = shadow?.comparison?.countertops
  const shadowFlooring = shadow?.result?.systems?.flooring
  const shadowFlooringComparison = shadow?.comparison?.flooring
  const shadowPaint = shadow?.result?.systems?.paint
  const shadowPaintComparison = shadow?.comparison?.paint
  const shadowAppliances = shadow?.result?.systems?.appliances
  const shadowApplianceComparison = shadow?.comparison?.appliances
  const shadowPlumbingFixtures = shadow?.result?.systems?.plumbingFixtures
  const shadowPlumbingFixturesComparison = shadow?.comparison?.plumbingFixtures
  const shadowPlumbing = shadow?.result?.systems?.plumbingRough
  const shadowPlumbingComparison = shadow?.comparison?.plumbingRough
  const shadowElectrical = shadow?.result?.systems?.electricalRough
  const shadowElectricalComparison = shadow?.comparison?.electricalRough
  const shadowInsulation = shadow?.result?.systems?.insulation
  const shadowInsulationComparison = shadow?.comparison?.insulation
  const shadowDoors = shadow?.result?.systems?.doors
  const shadowDrywall = shadow?.result?.systems?.drywall
  const shadowDoorComparison = shadow?.comparison?.doors
  const shadowComparison = shadow?.comparison?.drywall
  const shadowWindows = shadow?.result?.systems?.windows
  const shadowWindowComparison = shadow?.comparison?.windows

  const patchItem = async (itemId: string, data: Record<string, any>) => {
    try {
      const token = await getToken()
      await apiFetch(`/api/projects/${projectId}/takeoff/${takeoff.id}/items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      })
    } catch { /* silent */ }
  }

  // Project meta — needed to scope client-side DFW pricing hints.
  //
  // We delegate the entire "is this project in the supported DFW
  // pricing region?" decision to `resolveProjectLocationScope()`, the
  // SAME resolver the server uses for `getScopedPricePatterns`. That
  // means:
  //   * DFW pricing applies ONLY when `metro === 'DFW'` AND
  //     `confidence === 'explicit'`.
  //   * `confidence === 'explicit'` requires state === 'TX' AND
  //     the city normalises to one of the seven supported DFW cities
  //     (dallas / frisco / plano / mckinney / allen / celina /
  //     prosper). Any other Texas city resolves to `metro: null`,
  //     `confidence: 'derived'` and does NOT receive DFW pricing.
  //   * Non-TX cities and missing locations resolve to `metro: null`
  //     and likewise do not receive DFW pricing.
  // Demo projects keep the DFW table active so the demo experience
  // stays populated; that gate is `isDemoProject(projectId)` and is
  // intentionally narrower than "TX project."
  const isDemoForPricing = isDemoProject(projectId)
  const { data: projectMeta } = useQuery<{ city: string | null; state: string | null }>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
      if (!res.ok) throw new Error(`API unavailable ${res.status}`)
      return res.json()
    },
    enabled: !isDemoForPricing && !!projectId,
    staleTime: 60_000,
  })
  // Run the resolver. It handles trim, case, "TX" vs "Texas", and
  // city normalisation internally so we don't repeat that logic here.
  const locationScope = resolveProjectLocationScope({
    city: projectMeta?.city ?? null,
    state: projectMeta?.state ?? null,
  })
  const isDfwPricingSupported =
    isDemoForPricing ||
    (locationScope.metro === 'DFW' && locationScope.confidence === 'explicit')
  const scopedEstimatedPrice = (name: string): number | null =>
    isDfwPricingSupported ? getEstimatedPrice(name) : null
  // Show the unsupported-location placeholder whenever:
  //   * we're not a demo project, AND
  //   * pricing is NOT in the supported DFW scope, AND
  //   * the project meta has actually loaded (so we don't flash the
  //     hint during the initial fetch).
  const showLocationHint = !isDemoForPricing && !isDfwPricingSupported && !!projectMeta

  const currentStatus = overrideStatus || takeoff.status
  const visibleItems = takeoff.items.filter(i => !deletedItems.has(i.id))
  // Group categories AND sort them by construction sequence (FOUNDATION → LANDSCAPING).
  // Unknown categories sink to the bottom via fallback order 99.
  const categories = sortCategoryKeys([...new Set(visibleItems.map(i => i.category))])

  // Parse heights from takeoff notes if available

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CATEGORIES — Single source of truth (ordered correctly)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const CATEGORIES = [
  { id: 'FOUNDATION', en: 'Foundation', es: 'Cimentación', color: '#6B7280', order: 1 },
  { id: 'FRAMING', en: 'Framing', es: 'Estructura', color: '#A16207', order: 2 },
  { id: 'ROOFING', en: 'Roofing', es: 'Techo', color: '#B91C1C', order: 3 },
  { id: 'ELECTRICAL_ROUGH', en: 'Electrical Rough', es: 'Eléctrico Rough', color: '#F59E0B', order: 4 },
  { id: 'PLUMBING_ROUGH', en: 'Plumbing Rough', es: 'Plomería Rough', color: '#06B6D4', order: 5 },
  { id: 'HVAC', en: 'HVAC', es: 'Climatización', color: '#0D9488', order: 6 },
  { id: 'INSULATION', en: 'Insulation', es: 'Aislamiento', color: '#EC4899', order: 7 },
  { id: 'DRYWALL', en: 'Drywall', es: 'Tablaroca', color: '#D4A574', order: 8 },
  { id: 'ELECTRICAL_FINISH', en: 'Electrical Finish', es: 'Eléctrico Finish', color: '#FBBF24', order: 9 },
  { id: 'PLUMBING_FINISH', en: 'Plumbing Finish', es: 'Plomería Finish', color: '#22D3EE', order: 10 },
  { id: 'SIDING', en: 'Siding', es: 'Revestimiento', color: '#059669', order: 11 },
  { id: 'WINDOWS_DOORS', en: 'Windows & Doors', es: 'Ventanas y Puertas', color: '#0EA5E9', order: 12 },
  { id: 'FLOORING', en: 'Flooring', es: 'Pisos', color: '#78350F', order: 13 },
  { id: 'PAINT', en: 'Paint', es: 'Pintura', color: '#7C3AED', order: 14 },
  { id: 'CABINETS', en: 'Cabinets', es: 'Gabinetes', color: '#EA580C', order: 15 },
  { id: 'COUNTERTOPS', en: 'Countertops', es: 'Encimeras', color: '#475569', order: 16 },
  { id: 'APPLIANCES', en: 'Appliances', es: 'Electrodomésticos', color: '#4338CA', order: 17 },
  { id: 'LANDSCAPING', en: 'Landscaping', es: 'Paisajismo', color: '#65A30D', order: 18 },
  { id: 'MISCELLANEOUS', en: 'Miscellaneous', es: 'Miscelánea', color: '#8B5CF6', order: 19 },
] as const

const getCategoryLabel = (categoryId: string, lang: 'en' | 'es'): string => {
  const cat = CATEGORIES.find(c => c.id === categoryId)
  return cat ? cat[lang] : categoryId
}

const getCategoryColor = (categoryId: string): string => {
  const cat = CATEGORIES.find(c => c.id === categoryId)
  return cat?.color || '#9E9890'
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  let heights: Record<string, string> | null = null
  try {
    if ((takeoff as any).heights) {
      heights = (takeoff as any).heights
    } else if (takeoff.items.length > 0 && (takeoff as any).notes) {
      const parsed = JSON.parse((takeoff as any).notes)
      if (parsed.heights) heights = parsed.heights
    }
  } catch { /* not JSON */ }

  const toggleItem = (id: string) => {
    const next = new Set(selectedItems)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelectedItems(next)
  }

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const deleteItem = async (itemId: string) => {
    try {
      const token = await getToken()
      await apiFetch(`/api/projects/${projectId}/takeoff/${takeoff.id}/items/${itemId}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
    } catch { /* endpoint may not exist */ }
    setDeletedItems(prev => new Set(prev).add(itemId))
    setSelectedItems(prev => { const next = new Set(prev); next.delete(itemId); return next })
  }

  const handleDeleteSelected = () => {
    selectedItems.forEach(id => deleteItem(id))
  }

  const handleApproveSelected = () => {
    setApprovedItems(prev => {
      const next = new Set(prev)
      selectedItems.forEach(id => next.add(id))
      return next
    })
    setSelectedItems(new Set())
  }

  const approveItem = (itemId: string) => {
    setApprovedItems(prev => new Set(prev).add(itemId))
  }

  const handleConfirmAll = async () => {
    onConfirm()
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/takeoff/${takeoff.id}`, {
        method: 'PATCH',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'APPROVED' })
      })
      if (res.ok) {
        setOverrideStatus('APPROVED')
        showToast(t('Takeoff Approved'))
        return
      }
    } catch { /* PATCH may not exist */ }
    setOverrideStatus('APPROVED')
    showToast(t('Takeoff Approved'))
  }

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden relative">
      {/* Toast */}
      {toast && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-green-600 text-white text-[13px] font-[800] px-4 py-2 rounded-full shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center">
        <h3 className="text-[13px] font-[800] text-[#1C1A17] flex-1">
          {t('Material Takeoff')}
        </h3>
        <span className="text-[12px] font-[700] text-[#9E9890] mr-3">
          {visibleItems.length} {t('items')} · {takeoff.processingMs ? `${(takeoff.processingMs / 1000).toFixed(1)}s` : ''}
        </span>
        <StatusBadge status={currentStatus} />
      </div>

      {/* Estimated prices disclaimer */}
      <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <span className="text-amber-500 text-[14px] leading-none mt-0.5">⚠️</span>
        <p className="text-[11px] font-[600] text-amber-700">
          {t('takeoff.estimatedPricesNote')}
        </p>
      </div>

      {/* Location-scoped pricing hint — shown when project meta is loaded
          but the project is outside the supported DFW pricing region.
          We use the resolver's `confidence` to pick honest copy:
            - `derived`  → state + city present but city is NOT in the
                           supported DFW allow-list (e.g. Austin TX,
                           Atlanta GA). The user already gave us
                           location; the right message is "pricing not
                           available for this location yet."
            - `unknown`  → not enough location data to resolve a
                           jurisdiction at all. The right message is
                           "add project location."
          Either way we never fabricate DFW prices for an
          unsupported / unknown location. */}
      {showLocationHint && (
        <div
          className="mx-4 mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 flex items-start gap-2"
          data-testid="takeoff-location-hint"
        >
          <span className="text-[#64748B] text-[14px] leading-none mt-0.5">ℹ️</span>
          <p className="text-[11px] font-[600] text-[#475569]">
            {locationScope.confidence === 'derived'
              ? (isEs
                ? 'La guía de precios aún no está disponible para esta ubicación.'
                : 'Pricing guidance is not available for this location yet.')
              : (isEs
                ? 'Agrega la ubicación del proyecto para mejorar la guía de precios.'
                : 'Add project location to improve pricing guidance.')}
          </p>
        </div>
      )}

      {showShadow && shadow && (
        <div className="mx-4 mt-3 border border-[#BFDBFE] bg-[#EFF6FF] rounded-lg p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-[800] text-[#1E3A8A]">
                {isEs ? 'Takeoff v2 Shadow' : 'Takeoff v2 Shadow'}
              </div>
              <div className="text-[10px] font-[600] text-[#4B5563] mt-0.5">
                {shadow?.result?.jurisdictionPackCity
                  ? (isEs
                    ? `Corrió con pack de ${shadow.result.jurisdictionPackCity} sin reemplazar el takeoff principal.`
                    : `Ran with ${shadow.result.jurisdictionPackCity} pack without replacing the main takeoff.`)
                  : (isEs
                    ? 'Corrió en paralelo sin reemplazar el takeoff principal.'
                    : 'Ran in parallel without replacing the main takeoff.')}
              </div>
            </div>
            {shadow?.result?.version && (
              <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-white text-[#06539B] border border-[#BFDBFE]">
                {shadow.result.version}
              </span>
            )}
          </div>

          {shadow?.error && (
            <div className="mt-2 text-[10px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2">
              {isEs ? 'Shadow con error:' : 'Shadow error:'} {shadow.error}
            </div>
          )}

          {shadowDrywall?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Drywall 1/2"' : '1/2" Drywall'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDrywall.normalized.halfInSheetCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowComparison?.v1.regularSheets ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Type X 5/8"' : '5/8" Type X'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDrywall.normalized.typeXSheetCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowComparison?.v1.typeXSheets ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Corner Bead' : 'Corner Bead'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDrywall.normalized.cornerBeadLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowComparison?.v1.cornerBeadLf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Shadow' : 'Shadow BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDrywall.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Compuesto:' : 'Compound:'} {shadowDrywall.normalized.jointCompoundBuckets}
                </div>
              </div>
            </div>
          )}

          {shadowExterior?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Cladding SF' : 'Cladding SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowExterior.normalized.orderAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowExteriorComparison?.v1.claddingAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Trim LF' : 'Trim LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowExterior.normalized.trimLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowExteriorComparison?.v1.trimLf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Housewrap' : 'Housewrap'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowExterior.normalized.housewrapRolls}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowExterior.normalized.weatherBarrierRequired
                    ? (isEs ? 'Barrera sí' : 'Barrier yes')
                    : (isEs ? 'Barrera n/a' : 'Barrier n/a')}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Exterior' : 'Exterior BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowExterior.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowExterior.normalized.hardiePreferred
                    ? 'Hardie/FC'
                    : 'Generic'}
                </div>
              </div>
            </div>
          )}

          {shadowRoofing?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Shingles SQ' : 'Shingle SQ'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowRoofing.normalized.shingleSquares}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowRoofingComparison?.v1.shingleSquares ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Underlayment' : 'Underlayment'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowRoofing.normalized.underlaymentRolls}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Starter:' : 'Starter:'} {shadowRoofing.normalized.starterBundles}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Ridge Vent LF' : 'Ridge Vent LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowRoofing.normalized.ridgeVentLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Drip edge:' : 'Drip edge:'} {shadowRoofing.normalized.dripEdgeLf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Techo' : 'Roofing BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowRoofing.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowRoofing.normalized.class4ImpactRecommended
                    ? (isEs ? 'Class 4 recomendado' : 'Class 4 recommended')
                    : (isEs ? 'Class 4 n/a' : 'Class 4 n/a')}
                </div>
              </div>
            </div>
          )}

          {shadowHvac?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Sistemas HVAC' : 'HVAC systems'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowHvac.normalized.systemUnitCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowHvacComparison?.v1.systemUnitCount ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Tonelaje' : 'Tonnage'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowHvac.normalized.tonnage}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  HP {shadowHvac.normalized.heatPumpCount} · AH {shadowHvac.normalized.airHandlerCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Duct / line LF' : 'Duct / line LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowHvac.normalized.ductLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Line set:' : 'Line set:'} {shadowHvac.normalized.refrigerantLineLf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM HVAC' : 'HVAC BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowHvac.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowHvac.normalized.mechanicalVentilationRequired
                    ? `${isEs ? 'Vent. mecánica' : 'Mech. ventilation'} · ${shadowHvac.normalized.efficiencyBaseline}`
                    : shadowHvac.normalized.efficiencyBaseline}
                </div>
              </div>
            </div>
          )}

          {shadowLightingFans?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Luz total' : 'Total lighting'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowLightingFans.normalized.totalFixtureCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowLightingFansComparison?.v1.totalFixtureCount ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Recessed / fans' : 'Recessed / fans'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowLightingFans.normalized.recessedLightCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Fans:' : 'Fans:'} {shadowLightingFans.normalized.ceilingFanCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Decorative / exhaust' : 'Decorative / exhaust'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowLightingFans.normalized.decorativeFixtureCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Exhaust:' : 'Exhaust:'} {shadowLightingFans.normalized.exhaustFanCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Luz' : 'Lighting BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowLightingFans.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Trim:' : 'Trim:'} {shadowLightingFans.normalized.deviceTrimCount}
                </div>
              </div>
            </div>
          )}

          {shadowCabinets?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Gabinetes totales' : 'Total cabinets'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCabinets.normalized.totalUnitCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowCabinetsComparison?.v1.totalUnitCount ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Base / wall' : 'Base / wall'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCabinets.normalized.baseCabinetCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Wall:' : 'Wall:'} {shadowCabinets.normalized.wallCabinetCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Vanity / island' : 'Vanity / island'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCabinets.normalized.vanityCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Island:' : 'Island:'} {shadowCabinets.normalized.islandCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM gabinetes' : 'Cabinets BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCabinets.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Run LF:' : 'Run LF:'} {shadowCabinets.normalized.runLf}
                </div>
              </div>
            </div>
          )}

          {shadowCountertops?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Cubiertas SF' : 'Countertops SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCountertops.normalized.totalAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowCountertopsComparison?.v1.totalAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Kitchen / vanity' : 'Kitchen / vanity'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCountertops.normalized.kitchenAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Vanity:' : 'Vanity:'} {shadowCountertops.normalized.vanityAreaSf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Island / splash' : 'Island / splash'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCountertops.normalized.islandAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Splash LF:' : 'Splash LF:'} {shadowCountertops.normalized.backsplashLf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM cubiertas' : 'Countertops BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowCountertops.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Cutouts:' : 'Cutouts:'} {shadowCountertops.normalized.sinkCutoutCount} · {isEs ? 'Seams:' : 'Seams:'} {shadowCountertops.normalized.seamAllowanceCount}
                </div>
              </div>
            </div>
          )}

          {shadowFlooring?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Pisos SF' : 'Flooring SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowFlooring.normalized.totalAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowFlooringComparison?.v1.totalAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Tile / LVP' : 'Tile / LVP'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowFlooring.normalized.tileAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  LVP: {shadowFlooring.normalized.lvpAreaSf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Carpet / wood' : 'Carpet / wood'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowFlooring.normalized.carpetAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Wood:' : 'Wood:'} {shadowFlooring.normalized.hardwoodAreaSf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM pisos' : 'Flooring BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowFlooring.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Waste:' : 'Waste:'} {shadowFlooring.normalized.wasteAreaSf} · {isEs ? 'Transitions:' : 'Transitions:'} {shadowFlooring.normalized.transitionCount}
                </div>
              </div>
            </div>
          )}

          {shadowPaint?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Pintura SF' : 'Paint SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPaint.normalized.paintableAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowPaintComparison?.v1.paintableAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Finish / primer' : 'Finish / primer'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPaint.normalized.finishGallons}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Primer:' : 'Primer:'} {shadowPaint.normalized.primerGallons}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Interior / exterior' : 'Interior / exterior'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPaint.normalized.interiorAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Ext:' : 'Ext:'} {shadowPaint.normalized.exteriorAreaSf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Pintura' : 'Paint BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPaint.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Caulk:' : 'Caulk:'} {shadowPaint.normalized.caulkTubeCount}
                </div>
              </div>
            </div>
          )}

          {shadowAppliances?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Appliances total' : 'Total appliances'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowAppliances.normalized.totalUnitCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowApplianceComparison?.v1.totalUnitCount ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Kitchen pkg' : 'Kitchen pkg'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowAppliances.normalized.kitchenPackageCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  Ref {shadowAppliances.normalized.refrigeratorCount} · DW {shadowAppliances.normalized.dishwasherCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Range / micro / hood' : 'Range / micro / hood'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowAppliances.normalized.rangeCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  MW {shadowAppliances.normalized.microwaveCount} · Hood {shadowAppliances.normalized.hoodCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Appliances' : 'Appliances BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowAppliances.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Laundry:' : 'Laundry:'} {shadowAppliances.normalized.laundryUnitCount}
                </div>
              </div>
            </div>
          )}

          {shadowPlumbingFixtures?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Fixtures totales' : 'Total fixtures'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbingFixtures.normalized.totalFixtureCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowPlumbingFixturesComparison?.v1.totalFixtureCount ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Toilets / sinks' : 'Toilets / sinks'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbingFixtures.normalized.toiletCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Sinks:' : 'Sinks:'} {shadowPlumbingFixtures.normalized.sinkCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Faucets / shower' : 'Faucets / shower'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbingFixtures.normalized.faucetCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Shower:' : 'Shower:'} {shadowPlumbingFixtures.normalized.showerTrimCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Fixtures' : 'Fixtures BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbingFixtures.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowPlumbingFixtures.normalized.finishSpecBaseline}
                </div>
              </div>
            </div>
          )}

          {shadowPlumbing?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Supply LF' : 'Supply LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbing.normalized.supplyLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowPlumbingComparison?.v1.supplyLf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Drain LF' : 'Drain LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbing.normalized.drainLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Vent:' : 'Vent:'} {shadowPlumbing.normalized.ventLf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Gas LF' : 'Gas LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbing.normalized.gasLineLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'WH:' : 'WH:'} {shadowPlumbing.normalized.waterHeaterCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Plomería' : 'Plumbing BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowPlumbing.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowPlumbing.normalized.drainDefault}
                </div>
              </div>
            </div>
          )}

          {shadowElectrical?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Cable LF' : 'Wire LF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowElectrical.normalized.wireLf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowElectricalComparison?.v1.wireLf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Paneles' : 'Panels'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowElectrical.normalized.panelCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Breakers:' : 'Breakers:'} {shadowElectrical.normalized.breakerCount}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Cajas rough' : 'Rough boxes'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowElectrical.normalized.roughBoxCount}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowElectrical.normalized.surgeProtectionRequired
                    ? (isEs ? 'SPD requerido' : 'SPD required')
                    : (isEs ? 'SPD n/a' : 'SPD n/a')}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Eléctrico' : 'Electrical BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowElectrical.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowElectrical.normalized.panelServiceBaseline || '—'}
                </div>
              </div>
            </div>
          )}

          {shadowInsulation?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Muro SF' : 'Wall SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowInsulation.normalized.wallAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowInsulationComparison?.v1.wallAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Ático SF' : 'Attic SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowInsulation.normalized.atticAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowInsulationComparison?.v1.atticAreaSf ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Spray foam SF' : 'Spray foam SF'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowInsulation.normalized.sprayFoamAreaSf}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Batt:' : 'Batt:'} {shadowInsulation.normalized.battAreaSf}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Aislante' : 'Insulation BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowInsulation.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowInsulation.normalized.wallRValue} · {shadowInsulation.normalized.atticRValue}
                </div>
              </div>
            </div>
          )}

          {shadowDoors?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Puertas totales' : 'Total doors'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDoors.normalized.totalUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowDoorComparison?.v1.totalUnits ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Interiores' : 'Interior'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDoors.normalized.interiorUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Ext:' : 'Ext:'} {shadowDoors.normalized.exteriorUnits}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Garage' : 'Garage'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDoors.normalized.garageDoorUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'Herrajes:' : 'Hardware:'} {shadowDoors.normalized.hardwareSets}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Puertas' : 'Doors BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowDoors.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowDoors.normalized.weatherstrippingRequired
                    ? (isEs ? 'Weatherstrip sí' : 'Weatherstrip yes')
                    : (isEs ? 'Weatherstrip n/a' : 'Weatherstrip n/a')}
                </div>
              </div>
            </div>
          )}

          {shadowWindows?.normalized && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Ventanas totales' : 'Total windows'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowWindows.normalized.totalUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowWindowComparison?.v1.totalUnits ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Operables' : 'Operable'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowWindows.normalized.operableUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {isEs ? 'v1:' : 'v1:'} {shadowWindowComparison?.v1.operableUnits ?? '—'}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'Fijas' : 'Fixed'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowWindows.normalized.fixedUnits}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowWindows.normalized.lowERequired
                    ? (isEs ? 'Low-E requerido' : 'Low-E required')
                    : (isEs ? 'Low-E por verificar' : 'Low-E verify')}
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] p-2.5">
                <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">{isEs ? 'BOM Ventanas' : 'Windows BOM'}</div>
                <div className="text-[14px] font-[900] text-[#1C1A17]">{shadowWindows.bom?.length || 0}</div>
                <div className="text-[9px] font-[700] text-[#6B7280] mt-0.5">
                  {shadowWindows.normalized.performanceBaseline || '—'}
                </div>
              </div>
            </div>
          )}

          {shadowComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta 1/2"' : '1/2" delta'}: <span className="text-[#1C1A17]">{shadowComparison.delta.regularSheets >= 0 ? '+' : ''}{shadowComparison.delta.regularSheets}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta Type X' : 'Type X delta'}: <span className="text-[#1C1A17]">{shadowComparison.delta.typeXSheets >= 0 ? '+' : ''}{shadowComparison.delta.typeXSheets}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta corner bead' : 'Corner bead delta'}: <span className="text-[#1C1A17]">{shadowComparison.delta.cornerBeadLf >= 0 ? '+' : ''}{shadowComparison.delta.cornerBeadLf}</span>
              </div>
            </div>
          )}

          {shadowExteriorComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta cladding' : 'Cladding delta'}: <span className="text-[#1C1A17]">{shadowExteriorComparison.delta.claddingAreaSf >= 0 ? '+' : ''}{shadowExteriorComparison.delta.claddingAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta trim' : 'Trim delta'}: <span className="text-[#1C1A17]">{shadowExteriorComparison.delta.trimLf >= 0 ? '+' : ''}{shadowExteriorComparison.delta.trimLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta housewrap' : 'Housewrap delta'}: <span className="text-[#1C1A17]">{shadowExteriorComparison.delta.housewrapRolls >= 0 ? '+' : ''}{shadowExteriorComparison.delta.housewrapRolls}</span>
              </div>
            </div>
          )}

          {shadowRoofingComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta shingles' : 'Shingle delta'}: <span className="text-[#1C1A17]">{shadowRoofingComparison.delta.shingleSquares >= 0 ? '+' : ''}{shadowRoofingComparison.delta.shingleSquares}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta ridge vent' : 'Ridge vent delta'}: <span className="text-[#1C1A17]">{shadowRoofingComparison.delta.ridgeVentLf >= 0 ? '+' : ''}{shadowRoofingComparison.delta.ridgeVentLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta drip edge' : 'Drip edge delta'}: <span className="text-[#1C1A17]">{shadowRoofingComparison.delta.dripEdgeLf >= 0 ? '+' : ''}{shadowRoofingComparison.delta.dripEdgeLf}</span>
              </div>
            </div>
          )}

          {shadowHvacComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta sistemas' : 'System delta'}: <span className="text-[#1C1A17]">{shadowHvacComparison.delta.systemUnitCount >= 0 ? '+' : ''}{shadowHvacComparison.delta.systemUnitCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta tonelaje' : 'Tonnage delta'}: <span className="text-[#1C1A17]">{shadowHvacComparison.delta.tonnage >= 0 ? '+' : ''}{shadowHvacComparison.delta.tonnage}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta duct' : 'Duct delta'}: <span className="text-[#1C1A17]">{shadowHvacComparison.delta.ductLf >= 0 ? '+' : ''}{shadowHvacComparison.delta.ductLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta line set' : 'Line set delta'}: <span className="text-[#1C1A17]">{shadowHvacComparison.delta.refrigerantLineLf >= 0 ? '+' : ''}{shadowHvacComparison.delta.refrigerantLineLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta thermostats' : 'Thermostat delta'}: <span className="text-[#1C1A17]">{shadowHvacComparison.delta.thermostatCount >= 0 ? '+' : ''}{shadowHvacComparison.delta.thermostatCount}</span>
              </div>
            </div>
          )}

          {shadowLightingFansComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta luz' : 'Lighting delta'}: <span className="text-[#1C1A17]">{shadowLightingFansComparison.delta.totalFixtureCount >= 0 ? '+' : ''}{shadowLightingFansComparison.delta.totalFixtureCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta recessed' : 'Recessed delta'}: <span className="text-[#1C1A17]">{shadowLightingFansComparison.delta.recessedLightCount >= 0 ? '+' : ''}{shadowLightingFansComparison.delta.recessedLightCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta fans' : 'Fan delta'}: <span className="text-[#1C1A17]">{shadowLightingFansComparison.delta.ceilingFanCount >= 0 ? '+' : ''}{shadowLightingFansComparison.delta.ceilingFanCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta decorative' : 'Decorative delta'}: <span className="text-[#1C1A17]">{shadowLightingFansComparison.delta.decorativeFixtureCount >= 0 ? '+' : ''}{shadowLightingFansComparison.delta.decorativeFixtureCount}</span>
              </div>
            </div>
          )}

          {shadowCabinetsComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta gabinetes' : 'Cabinet delta'}: <span className="text-[#1C1A17]">{shadowCabinetsComparison.delta.totalUnitCount >= 0 ? '+' : ''}{shadowCabinetsComparison.delta.totalUnitCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta base' : 'Base delta'}: <span className="text-[#1C1A17]">{shadowCabinetsComparison.delta.baseCabinetCount >= 0 ? '+' : ''}{shadowCabinetsComparison.delta.baseCabinetCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta wall' : 'Wall delta'}: <span className="text-[#1C1A17]">{shadowCabinetsComparison.delta.wallCabinetCount >= 0 ? '+' : ''}{shadowCabinetsComparison.delta.wallCabinetCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta run LF' : 'Run LF delta'}: <span className="text-[#1C1A17]">{shadowCabinetsComparison.delta.runLf >= 0 ? '+' : ''}{shadowCabinetsComparison.delta.runLf}</span>
              </div>
            </div>
          )}

          {shadowCountertopsComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta cubiertas SF' : 'Countertop SF delta'}: <span className="text-[#1C1A17]">{shadowCountertopsComparison.delta.totalAreaSf >= 0 ? '+' : ''}{shadowCountertopsComparison.delta.totalAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta kitchen SF' : 'Kitchen SF delta'}: <span className="text-[#1C1A17]">{shadowCountertopsComparison.delta.kitchenAreaSf >= 0 ? '+' : ''}{shadowCountertopsComparison.delta.kitchenAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta vanity SF' : 'Vanity SF delta'}: <span className="text-[#1C1A17]">{shadowCountertopsComparison.delta.vanityAreaSf >= 0 ? '+' : ''}{shadowCountertopsComparison.delta.vanityAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta splash LF' : 'Splash LF delta'}: <span className="text-[#1C1A17]">{shadowCountertopsComparison.delta.backsplashLf >= 0 ? '+' : ''}{shadowCountertopsComparison.delta.backsplashLf}</span>
              </div>
            </div>
          )}

          {shadowFlooringComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta pisos SF' : 'Flooring SF delta'}: <span className="text-[#1C1A17]">{shadowFlooringComparison.delta.totalAreaSf >= 0 ? '+' : ''}{shadowFlooringComparison.delta.totalAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta tile SF' : 'Tile SF delta'}: <span className="text-[#1C1A17]">{shadowFlooringComparison.delta.tileAreaSf >= 0 ? '+' : ''}{shadowFlooringComparison.delta.tileAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta LVP SF' : 'LVP SF delta'}: <span className="text-[#1C1A17]">{shadowFlooringComparison.delta.lvpAreaSf >= 0 ? '+' : ''}{shadowFlooringComparison.delta.lvpAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta transitions' : 'Transition delta'}: <span className="text-[#1C1A17]">{shadowFlooringComparison.delta.transitionCount >= 0 ? '+' : ''}{shadowFlooringComparison.delta.transitionCount}</span>
              </div>
            </div>
          )}

          {shadowPaintComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta pintura SF' : 'Paint SF delta'}: <span className="text-[#1C1A17]">{shadowPaintComparison.delta.paintableAreaSf >= 0 ? '+' : ''}{shadowPaintComparison.delta.paintableAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta finish gal' : 'Finish gal delta'}: <span className="text-[#1C1A17]">{shadowPaintComparison.delta.finishGallons >= 0 ? '+' : ''}{shadowPaintComparison.delta.finishGallons}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta primer gal' : 'Primer gal delta'}: <span className="text-[#1C1A17]">{shadowPaintComparison.delta.primerGallons >= 0 ? '+' : ''}{shadowPaintComparison.delta.primerGallons}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta caulk' : 'Caulk delta'}: <span className="text-[#1C1A17]">{shadowPaintComparison.delta.caulkTubeCount >= 0 ? '+' : ''}{shadowPaintComparison.delta.caulkTubeCount}</span>
              </div>
            </div>
          )}

          {shadowApplianceComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta appliances' : 'Appliance delta'}: <span className="text-[#1C1A17]">{shadowApplianceComparison.delta.totalUnitCount >= 0 ? '+' : ''}{shadowApplianceComparison.delta.totalUnitCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta kitchen pkg' : 'Kitchen pkg delta'}: <span className="text-[#1C1A17]">{shadowApplianceComparison.delta.kitchenPackageCount >= 0 ? '+' : ''}{shadowApplianceComparison.delta.kitchenPackageCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta laundry' : 'Laundry delta'}: <span className="text-[#1C1A17]">{shadowApplianceComparison.delta.laundryUnitCount >= 0 ? '+' : ''}{shadowApplianceComparison.delta.laundryUnitCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta range/ref' : 'Range/ref delta'}: <span className="text-[#1C1A17]">{shadowApplianceComparison.delta.rangeCount >= 0 ? '+' : ''}{shadowApplianceComparison.delta.rangeCount} / {shadowApplianceComparison.delta.refrigeratorCount >= 0 ? '+' : ''}{shadowApplianceComparison.delta.refrigeratorCount}</span>
              </div>
            </div>
          )}

          {shadowPlumbingFixturesComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta fixtures' : 'Fixture delta'}: <span className="text-[#1C1A17]">{shadowPlumbingFixturesComparison.delta.totalFixtureCount >= 0 ? '+' : ''}{shadowPlumbingFixturesComparison.delta.totalFixtureCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta toilets' : 'Toilet delta'}: <span className="text-[#1C1A17]">{shadowPlumbingFixturesComparison.delta.toiletCount >= 0 ? '+' : ''}{shadowPlumbingFixturesComparison.delta.toiletCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta faucets' : 'Faucet delta'}: <span className="text-[#1C1A17]">{shadowPlumbingFixturesComparison.delta.faucetCount >= 0 ? '+' : ''}{shadowPlumbingFixturesComparison.delta.faucetCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta shower' : 'Shower delta'}: <span className="text-[#1C1A17]">{shadowPlumbingFixturesComparison.delta.showerTrimCount >= 0 ? '+' : ''}{shadowPlumbingFixturesComparison.delta.showerTrimCount}</span>
              </div>
            </div>
          )}

          {shadowPlumbingComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta supply' : 'Supply delta'}: <span className="text-[#1C1A17]">{shadowPlumbingComparison.delta.supplyLf >= 0 ? '+' : ''}{shadowPlumbingComparison.delta.supplyLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta drain' : 'Drain delta'}: <span className="text-[#1C1A17]">{shadowPlumbingComparison.delta.drainLf >= 0 ? '+' : ''}{shadowPlumbingComparison.delta.drainLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta vent' : 'Vent delta'}: <span className="text-[#1C1A17]">{shadowPlumbingComparison.delta.ventLf >= 0 ? '+' : ''}{shadowPlumbingComparison.delta.ventLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta gas' : 'Gas delta'}: <span className="text-[#1C1A17]">{shadowPlumbingComparison.delta.gasLineLf >= 0 ? '+' : ''}{shadowPlumbingComparison.delta.gasLineLf}</span>
              </div>
            </div>
          )}

          {shadowElectricalComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta cable' : 'Wire delta'}: <span className="text-[#1C1A17]">{shadowElectricalComparison.delta.wireLf >= 0 ? '+' : ''}{shadowElectricalComparison.delta.wireLf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta paneles' : 'Panel delta'}: <span className="text-[#1C1A17]">{shadowElectricalComparison.delta.panelCount >= 0 ? '+' : ''}{shadowElectricalComparison.delta.panelCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta breakers' : 'Breaker delta'}: <span className="text-[#1C1A17]">{shadowElectricalComparison.delta.breakerCount >= 0 ? '+' : ''}{shadowElectricalComparison.delta.breakerCount}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta cajas' : 'Box delta'}: <span className="text-[#1C1A17]">{shadowElectricalComparison.delta.roughBoxCount >= 0 ? '+' : ''}{shadowElectricalComparison.delta.roughBoxCount}</span>
              </div>
            </div>
          )}

          {shadowInsulationComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta muro' : 'Wall delta'}: <span className="text-[#1C1A17]">{shadowInsulationComparison.delta.wallAreaSf >= 0 ? '+' : ''}{shadowInsulationComparison.delta.wallAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta ático' : 'Attic delta'}: <span className="text-[#1C1A17]">{shadowInsulationComparison.delta.atticAreaSf >= 0 ? '+' : ''}{shadowInsulationComparison.delta.atticAreaSf}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta spray foam' : 'Spray foam delta'}: <span className="text-[#1C1A17]">{shadowInsulationComparison.delta.sprayFoamAreaSf >= 0 ? '+' : ''}{shadowInsulationComparison.delta.sprayFoamAreaSf}</span>
              </div>
            </div>
          )}

          {shadowDoorComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta puertas' : 'Door delta'}: <span className="text-[#1C1A17]">{shadowDoorComparison.delta.totalUnits >= 0 ? '+' : ''}{shadowDoorComparison.delta.totalUnits}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta exteriores' : 'Exterior delta'}: <span className="text-[#1C1A17]">{shadowDoorComparison.delta.exteriorUnits >= 0 ? '+' : ''}{shadowDoorComparison.delta.exteriorUnits}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta garage' : 'Garage delta'}: <span className="text-[#1C1A17]">{shadowDoorComparison.delta.garageDoorUnits >= 0 ? '+' : ''}{shadowDoorComparison.delta.garageDoorUnits}</span>
              </div>
            </div>
          )}

          {shadowWindowComparison?.delta && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px]">
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta ventanas' : 'Window delta'}: <span className="text-[#1C1A17]">{shadowWindowComparison.delta.totalUnits >= 0 ? '+' : ''}{shadowWindowComparison.delta.totalUnits}</span>
              </div>
              <div className="rounded-lg bg-white border border-[#DBEAFE] px-2.5 py-2 font-[700] text-[#4B5563]">
                {isEs ? 'Delta operables' : 'Operable delta'}: <span className="text-[#1C1A17]">{shadowWindowComparison.delta.operableUnits >= 0 ? '+' : ''}{shadowWindowComparison.delta.operableUnits}</span>
              </div>
            </div>
          )}

          {shadow?.facts?.systems?.exterior?.notes?.length || shadow?.facts?.systems?.roofing?.notes?.length || shadow?.facts?.systems?.hvac?.notes?.length || shadow?.facts?.systems?.lightingFans?.notes?.length || shadow?.facts?.systems?.cabinets?.notes?.length || shadow?.facts?.systems?.countertops?.notes?.length || shadow?.facts?.systems?.flooring?.notes?.length || shadow?.facts?.systems?.paint?.notes?.length || shadow?.facts?.systems?.appliances?.notes?.length || shadow?.facts?.systems?.plumbingFixtures?.notes?.length || shadow?.facts?.systems?.plumbingRough?.notes?.length || shadow?.facts?.systems?.electricalRough?.notes?.length || shadow?.facts?.systems?.insulation?.notes?.length || shadow?.facts?.systems?.drywall?.notes?.length || shadow?.facts?.systems?.doors?.notes?.length || shadow?.facts?.systems?.windows?.notes?.length ? (
            <div className="mt-2 text-[10px] font-[600] text-[#4B5563]">
              {[
                ...(shadow?.facts?.systems?.exterior?.notes || []),
                ...(shadow?.facts?.systems?.roofing?.notes || []),
                ...(shadow?.facts?.systems?.hvac?.notes || []),
                ...(shadow?.facts?.systems?.lightingFans?.notes || []),
                ...(shadow?.facts?.systems?.cabinets?.notes || []),
                ...(shadow?.facts?.systems?.countertops?.notes || []),
                ...(shadow?.facts?.systems?.flooring?.notes || []),
                ...(shadow?.facts?.systems?.paint?.notes || []),
                ...(shadow?.facts?.systems?.appliances?.notes || []),
                ...(shadow?.facts?.systems?.plumbingFixtures?.notes || []),
                ...(shadow?.facts?.systems?.plumbingRough?.notes || []),
                ...(shadow?.facts?.systems?.electricalRough?.notes || []),
                ...(shadow?.facts?.systems?.insulation?.notes || []),
                ...(shadow?.facts?.systems?.drywall?.notes || []),
                ...(shadow?.facts?.systems?.doors?.notes || []),
                ...(shadow?.facts?.systems?.windows?.notes || []),
              ].join(' · ')}
            </div>
          ) : null}
        </div>
      )}

      {/* Selected items action bar — sticky */}
      {selectedItems.size > 0 && (
        <div className="sticky top-0 z-10 px-4 py-2.5 border-b border-[#DBEAFE] bg-[#1C1A17] flex items-center justify-between">
          <span className="text-[11px] font-[800] text-white">{selectedItems.size} {t('items selected')}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleApproveSelected}
              className="flex items-center gap-1.5 bg-green-500 text-white text-[13px] font-[800] px-3 py-1.5 rounded-full hover:bg-green-600 transition-colors"
            >
              <CheckCircle size={12} />
              {isEs ? 'Aprobar seleccionados' : 'Approve selected'}
            </button>
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1.5 bg-white text-red-600 text-[13px] font-[800] px-3 py-1.5 rounded-full hover:bg-red-50 transition-colors"
            >
              <Trash2 size={12} />
              {isEs ? 'Eliminar seleccionados' : 'Delete selected'}
            </button>
          </div>
        </div>
      )}

      {/* Categories */}
      {categories.map(cat => {
        const catItems = visibleItems.filter(i => i.category === cat)
        const normalizedCat = cat.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_').toUpperCase()
        const label = CATEGORY_LABELS[cat] || CATEGORY_LABELS[cat.toUpperCase()] || CATEGORY_LABELS[cat.replace(/\s+/g, '_').toUpperCase()] || CATEGORY_LABELS[normalizedCat]
        const catColor = CATEGORY_COLORS[cat] || label?.color || '#94A3B8'
        const expanded = expandedCategories.has(cat)

        return (
          <div key={cat} className="border-t border-[#ECEAE4]" style={{ borderLeft: `4px solid ${catColor}` }}>
            <div
              onClick={() => onToggleCategory(cat)}
              className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-[#F2F0EB] transition-colors"
            >
              <div className="w-[8px] h-[8px] rounded-[2px]" style={{ background: catColor }} />
              <span className="text-[12px] font-[700] flex-1">
                {(isEs ? label?.es : label?.en) || cat}
              </span>
              <span className="text-[12px] font-[700] text-[#9E9890]">
                {catItems.length} {t('items')}
              </span>
              {expanded ? <ChevronUp size={14} className="text-[#9E9890]" /> : <ChevronDown size={14} className="text-[#9E9890]" />}
            </div>

            {expanded && (
              <div className="overflow-x-auto">
                {/* Waste factor bar */}
                {(() => {
                  const wf = WASTE_FACTORS[cat] || WASTE_FACTORS[cat.toUpperCase()]
                  if (!wf) return null
                  // Through the model rule, NOT a raw reduce: this sum is quantity-for-waste display, never a purchasable total.
                  const totalQty = nonPurchasableQuantitySum(catItems, (i) => i.quantity, 'waste-factor display — legacy takeoff, never money')
                  const wasteQty = Math.ceil(totalQty * wf.pct / 100)
                  return (
                    <div className="flex items-center gap-3 px-4 py-2 bg-amber-50/50 border-b border-amber-100 text-[10px]">
                      <span className="font-[700] text-amber-700">⚠ {t('Waste Factor')}: +{wf.pct}%</span>
                      <span className="font-[600] text-amber-600">{wf.label}</span>
                      <span className="ml-auto font-[800] text-amber-800">
                        {totalQty.toLocaleString()} + {wasteQty.toLocaleString()} = <strong>{(totalQty + wasteQty).toLocaleString()}</strong> {t('to order')}
                      </span>
                    </div>
                  )
                })()}
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="sticky top-0 bg-[#F2F0EB] text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                      <th className="w-8 py-1.5 px-2"><input type="checkbox" className="accent-[#1C1A17]" onChange={(e) => { setSelectedItems(prev => { const next = new Set(prev); catItems.forEach(i => e.target.checked ? next.add(i.id) : next.delete(i.id)); return next }) }} /></th>
                      <th className="w-8 py-1.5 text-center">#</th>
                      <th className="py-1.5 px-2 text-left" style={{ width: '50%' }}>{t('Description')}</th>
                      <th className="py-1.5 px-2 text-right w-16">{t('Qty')}</th>
                      <th className="py-1.5 px-2 text-left w-12">{t('Unit')}</th>
                      <th className="py-1.5 px-2 text-right w-20">{t('Unit Price')}</th>
                      <th className="py-1.5 px-2 text-right w-20">{t('Total')}</th>
                      <th className="py-1.5 px-2 text-center w-16">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catItems.map((item, idx) => {
                      const isItemApproved = item.isApproved || approvedItems.has(item.id)
                      return (
                        <tr key={item.id} className="border-t border-[#ECEAE4] hover:bg-[#FAFAF8] transition-colors group">
                          <td className="py-1.5 px-2 text-center">
                            <input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleItem(item.id)} className="accent-[#1C1A17]" />
                          </td>
                          <td className="py-1.5 text-center text-[10px] font-[600] text-[#9E9890]">{idx + 1}</td>
                          <td className="py-1.5 px-2 relative">
                            <div className="font-[700] text-[#1C1A17]">{editedNames[item.id] || item.name}</div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {(item as any).sheet && <span className="text-[9px] font-[600] text-gray-400">{(item as any).sheet}</span>}
                              {((item as any).notes || item.description) && <span className="text-[9px] font-[600] text-gray-500">{(item as any).notes || item.description}</span>}
                              {(() => {
                                const itemCategory = (item.category || '').toUpperCase()
                                const selectionBoardOwnedCategory = itemCategory === 'EXTERIOR' || itemCategory === 'SIDING'
                                // Merge DB alternatives (objects or strings) with hardcoded fallback
                                const dbAlts = item.alternatives || []
                                const fallbackAlts = findAlternatives(item.name)
                                const hasAlts = dbAlts.length > 0 || fallbackAlts.length > 0
                                if (selectionBoardOwnedCategory) {
                                  return (
                                    <span className="text-[8px] font-[700] text-[#06539B] bg-[#EFF6FF] px-1.5 py-0.5 rounded-full">
                                      {isEs ? 'Ver variantes en Selections' : 'See finish options in Selections'}
                                    </span>
                                  )
                                }
                                if (!hasAlts) return null
                                return (
                                  <span className="relative">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setOpenAltDropdown(openAltDropdown === item.id ? null : item.id) }}
                                      className="text-[8px] font-[700] text-[#2196F3] hover:text-[#1976D2] bg-blue-50 px-1.5 py-0.5 rounded-full"
                                    >
                                      {t('Alternatives')} ▾
                                    </button>
                                    {openAltDropdown === item.id && (
                                      <div className="absolute top-5 left-0 z-20 bg-white border border-[#ECEAE4] rounded-lg shadow-lg py-1 min-w-[260px]">
                                        {dbAlts.length > 0 ? (
                                          dbAlts.map((alt, i) => {
                                            const isObj = typeof alt === 'object' && alt !== null
                                            const altName = isObj ? (alt as TakeoffAlternative).name : (alt as string)
                                            const altCost = isObj ? (alt as TakeoffAlternative).estimatedCost : null
                                            const altUnit = isObj ? (alt as TakeoffAlternative).unit : null
                                            const altNotes = isObj ? (alt as TakeoffAlternative).notes : null
                                            return (
                                              <button
                                                key={i}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  setEditedNames(prev => ({ ...prev, [item.id]: altName }))
                                                  if (altCost != null) setEditedPrices(prev => ({ ...prev, [item.id]: altCost }))
                                                  patchItem(item.id, { name: altName, ...(altCost != null ? { unitPrice: altCost } : {}) })
                                                  setOpenAltDropdown(null)
                                                  showToast(`${t('Switched to')} ${altName}`)
                                                }}
                                                className="block w-full text-left px-3 py-1.5 hover:bg-[#F2F0EB] transition-colors"
                                              >
                                                <div className="text-[11px] font-[700] text-[#1C1A17]">{altName}</div>
                                                {(altCost != null || altNotes) && (
                                                  <div className="flex items-center gap-2 mt-0.5">
                                                    {altCost != null && <span className="text-[9px] font-[700] text-[#4CAF50]">${altCost}/{altUnit || item.unit}</span>}
                                                    {altNotes && <span className="text-[9px] font-[600] text-[#9E9890]">{altNotes}</span>}
                                                  </div>
                                                )}
                                              </button>
                                            )
                                          })
                                        ) : (
                                          fallbackAlts.map(alt => {
                                            const altPrice = scopedEstimatedPrice(alt)
                                            return (
                                            <button
                                              key={alt}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                setEditedNames(prev => ({ ...prev, [item.id]: alt }))
                                                if (altPrice != null) setEditedPrices(prev => ({ ...prev, [item.id]: altPrice }))
                                                patchItem(item.id, { name: alt, ...(altPrice != null ? { unitPrice: altPrice } : {}) })
                                                setOpenAltDropdown(null)
                                                showToast(`${t('Switched to')} ${alt}`)
                                              }}
                                              className="block w-full text-left px-3 py-1.5 hover:bg-[#F2F0EB] transition-colors"
                                            >
                                              <div className="text-[11px] font-[700] text-[#1C1A17]">{alt}</div>
                                              {altPrice != null && (
                                                <div className="text-[9px] font-[700] text-[#4CAF50] mt-0.5">${altPrice}/{item.unit}</div>
                                              )}
                                            </button>
                                            )
                                          })
                                        )}
                                      </div>
                                    )}
                                  </span>
                                )
                              })()}
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right font-[800] text-[#1C1A17]">{item.quantity.toLocaleString()}</td>
                          <td className="py-1.5 px-2 text-[#9E9890] font-[600]">{item.unit}</td>
                          <td className="py-1.5 px-2 text-right">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="—"
                              value={editedPrices[item.id] ?? item.unitPrice ?? scopedEstimatedPrice(item.name) ?? ''}
                              onChange={(e) => setEditedPrices(prev => ({ ...prev, [item.id]: parseFloat(e.target.value) || 0 }))}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value)
                                if (!isNaN(val)) patchItem(item.id, { unitPrice: val })
                              }}
                              className="w-16 text-right text-[12px] font-[700] bg-transparent border-b border-dashed border-[#ECEAE4] focus:border-[#2196F3] focus:outline-none py-0.5 text-[#1C1A17] placeholder:text-[#9E9890]"
                            />
                          </td>
                          <td className="py-1.5 px-2 text-right text-[#1C1A17] font-[700]">
                            {(() => {
                              const price = editedPrices[item.id] ?? item.unitPrice ?? scopedEstimatedPrice(item.name)
                              return price ? `$${(item.quantity * price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'
                            })()}
                          </td>
                          <td className="py-1.5 px-2 text-center relative">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => setPriceCompareId(priceCompareId === item.id ? null : item.id)} title={t('Compare Prices')}>
                                <ShoppingCart size={13} className="text-gray-300 hover:text-[#F5A623] transition-colors" />
                              </button>
                              {isItemApproved ? (
                                <CheckCircle size={14} className="text-green-500" />
                              ) : (
                                <button onClick={() => approveItem(item.id)} title={t('Approve')}>
                                  <CheckCircle size={14} className="text-gray-300 hover:text-green-500 transition-colors" />
                                </button>
                              )}
                              <button onClick={() => deleteItem(item.id)} title={t('Delete')} className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <Trash2 size={13} className="text-gray-300 hover:text-red-500 transition-colors" />
                              </button>
                            </div>
                            {priceCompareId === item.id && (
                              <div className="absolute right-0 top-full z-30 bg-white border border-[#ECEAE4] rounded-xl shadow-lg p-3 min-w-[240px] text-left" onClick={e => e.stopPropagation()}>
                                <div className="text-[10px] font-[800] text-[#1C1A17] mb-2">{isEs ? 'Comparación de precios' : 'Price comparison'}</div>
                                <p className="text-[11px] font-[600] text-[#6B6660] leading-snug">
                                  {isEs
                                    ? 'La comparación de precios aún no está disponible. Carga cotizaciones verificadas de proveedores para comparar precios.'
                                    : 'Price comparison is not available yet. Upload verified supplier quotes to compare pricing.'}
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}

      {/* Confirm button — disabled while legacy quantity approval is
          contained. The button remains in the layout (no design change)
          but the click is gated and the label communicates the
          reference-only posture. */}
      {currentStatus === 'NEEDS_REVIEW' && (
        <div className="px-4 py-3 border-t border-[#ECEAE4] bg-[#FAFAF8]">
          {legacyApprovalAllowed ? (
            <button
              onClick={handleConfirmAll}
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-extrabold w-full py-3 rounded-xl tracking-wide disabled:opacity-40 transition-colors"
            >
              {isConfirming ? '...' : t('Confirm & Approve All')}
            </button>
          ) : (
            <button
              type="button"
              disabled
              aria-disabled="true"
              title={t('Approval is disabled while takeoff is upgraded')}
              className="w-full bg-white text-[#9E9890] border border-[#ECEAE4] text-sm font-extrabold py-3 rounded-xl tracking-wide cursor-not-allowed"
            >
              {t('Reference only — approval disabled during upgrade')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── HELPERS ───────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const styles: Record<string, string> = {
    PENDING:        'bg-[#F2F0EB] text-[#9E9890]',
    AI_PROCESSING:  'bg-amber-50 text-amber-600',
    NEEDS_REVIEW:   'bg-sky-50 text-sky-600',
    APPROVED:       'bg-green-100 text-green-700',
    CONFIRMED:      'bg-green-100 text-green-700',
    FAILED:         'bg-red-50 text-red-600',
    ERROR:          'bg-red-50 text-red-600',
  }
  const labels: Record<string, string> = isEs ? {
    PENDING: 'PENDIENTE', AI_PROCESSING: 'PROCESANDO', NEEDS_REVIEW: 'REVISIÓN PENDIENTE',
    APPROVED: 'APROBADO', CONFIRMED: 'CONFIRMADO', FAILED: 'FALLIDO', ERROR: 'ERROR',
  } : {}
  return (
    <span className={`text-[9px] font-[800] px-2.5 py-1 rounded-full ${styles[status] || styles.PENDING}`}>
      {labels[status] || status}
    </span>
  )
}

function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence == null) return null
  const pct = Math.round(confidence * 100)
  const color = pct >= 90 ? 'text-[#22C55E]' : pct >= 70 ? 'text-amber-500' : 'text-red-500'
  return <span className={`text-[10px] font-[800] ${color}`}>{pct}%</span>
}
